import React, { useState, useEffect, useRef } from 'react';
import { mergeYearGoals, mergeYearGoalNodes, formatGoalCurrency } from "../lib/goalWording";
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Target, Lightning as Zap, Calendar, ArrowRight, ArrowLeft, DownloadSimple as Download, CurrencyDollar as DollarSign, Clock, PiggyBank, CreditCard, CaretRight as ChevronRight, Palette, CheckCircle as CheckCircle2, Sparkle as Sparkles, Lock, X } from "@phosphor-icons/react";
import { WEEKS_OFF_OPTIONS, SELF_EMPLOYMENT_TAX } from '../lib/incomeMapShared';
import ScenarioCompare from '../components/ScenarioCompare';
import { CalculatorGate } from '../components/CalculatorGate';
import { ConfirmNotice } from '../components/ConfirmNotice';
import DebtStrategistModal from '../components/pitcrew/DebtStrategistModal';
import SavingsSplitterModal from '../components/pitcrew/SavingsSplitterModal';
import PtoBufferModal from '../components/pitcrew/PtoBufferModal';
import { SiteFooter } from '../components/SiteFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// One pace, all year. Used when every goal runs the full 12 months (a 12-month debt
// payoff, for example), so there is no second phase and no "after the debt" options.
const SinglePaceTrajectory = ({ weeklyTarget, dailyTarget, daysPerWeek, workWeeks, setAsides,
                                timeOffBufferPerWeek, formatCurrency }) => {
  const weeksOff = 52 - workWeeks;
  const totalTimeOffFund = timeOffBufferPerWeek * workWeeks;
  return (
    <div className="max-w-4xl mx-auto mt-16" data-testid="timeline-section">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: 'rgba(46, 134, 171, 0.15)' }}>
          <Calendar className="w-4 h-4" style={{ color: '#2E86AB' }} />
          <span className="font-body text-sm" style={{ color: '#2E86AB' }}>Your 12-Month Trajectory</span>
        </div>
        <h3 className="font-heading text-2xl sm:text-3xl font-medium mb-2" style={{ color: '#2A1E5C' }}>One pace, all year.</h3>
        <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>
          Your goals run the full 12 months, so your target holds steady from week 1 to week {workWeeks}.
        </p>
      </div>

      <div className="relative h-3 rounded-full overflow-hidden mb-8" style={{ background: 'rgba(42, 30, 92, 0.1)' }}>
        <div className="absolute h-full w-full" style={{ background: 'linear-gradient(to right, #2E86AB 0%, #4CAF50 100%)' }} />
      </div>

      <div className="p-8 rounded-xl" style={{ background: 'rgba(46, 134, 171, 0.08)', border: '2px solid #2E86AB' }}>
        <div className="text-center mb-6">
          <p className="font-body text-xs uppercase tracking-wider mb-3" style={{ color: '#2E86AB' }}>Your Weekly Target</p>
          <p className="font-heading text-5xl font-medium mb-2" style={{ color: '#2A1E5C' }}>{formatCurrency(weeklyTarget)}</p>
          <p className="font-heading text-xl" style={{ color: '#2E86AB' }}>{formatCurrency(dailyTarget)}/day × {daysPerWeek} days</p>
        </div>

        <div className="pt-6 border-t space-y-2" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
          <p className="font-body text-xs mb-2" style={{ color: '#8B7E6A' }}>Each week you're setting aside:</p>
          {setAsides.map((goal) => (
            <div key={goal.name} className="flex justify-between items-center">
              <span className="font-body text-sm" style={{ color: '#5D5A6B' }}>{goal.name}</span>
              <span className="font-body text-sm font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(goal.weeklyAmount)}/wk</span>
            </div>
          ))}
          {weeksOff > 0 && (
            <div className="flex justify-between items-center">
              <span className="font-body text-sm" style={{ color: '#5D5A6B' }}>Time off</span>
              <span className="font-body text-sm font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(timeOffBufferPerWeek)}/wk</span>
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-6 pt-6 border-t" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
          {setAsides.map((goal) => (
            <div key={`year-end-${goal.name}`} className="p-4 rounded-lg text-center" style={{ background: 'rgba(76, 175, 80, 0.12)' }}>
              <p className="font-body text-xs mb-1" style={{ color: '#8B7E6A' }}>{goal.name} by month 12</p>
              <p className="font-heading text-2xl font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(goal.totalAmount)}</p>
              <p className="font-body text-xs mt-1" style={{ color: '#4CAF50' }}>Goal met</p>
            </div>
          ))}
          {weeksOff > 0 && (
            <div className="p-4 rounded-lg text-center" style={{ background: 'rgba(245, 203, 92, 0.18)' }}>
              <p className="font-body text-xs mb-1" style={{ color: '#8B7E6A' }}>Your {weeksOff}-week time-off fund</p>
              <p className="font-heading text-2xl font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(totalTimeOffFund)}</p>
              <p className="font-body text-xs mt-1" style={{ color: '#8B7E6A' }}>{formatCurrency(totalTimeOffFund / weeksOff)} for each week off</p>
            </div>
          )}
        </div>

        <p className="font-body text-sm text-center mt-6" style={{ color: '#5D5A6B' }}>
          Want a lighter second half of the year? Shorten a goal's timeline and your trajectory splits into
          phases, with a lower target once the first goal is cleared.
        </p>
      </div>
      <p className="font-body text-sm text-center mt-4" style={{ color: '#8B7E6A' }}>
        Beat your target in any week? That extra lands in your bonus bank.
      </p>
    </div>
  );
};

const RelayCalculatorPage = ({ embedded = false, seasonSetupMode = false, onboardingMode = false, pitStopMode = false, midQuarterChange = false, quarterKey = '', hasSeasonPass = false } = {}) => {
  // Access gate state
  const [accessEmail, setAccessEmail] = useState('');
  const [access, setAccess] = useState(embedded ? 'granted' : 'checking');

  // Email-my-results state
  const [resultEmail, setResultEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [seasonSaving, setSeasonSaving] = useState(false);
  const [lockSaving, setLockSaving] = useState(false);
  const [lockConfirmOpen, setLockConfirmOpen] = useState(false);
  const [activeTool, setActiveTool] = useState(null);

  // Mid-quarter warning. Never shown on arrival, only the first time a racer who is
  // already mid-quarter actually edits one of their locked numbers, once per quarter.
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const initialForm = useRef(null);
  const ackKey = `pr.relay.midquarter.ack.${quarterKey || 'none'}`;

  // Post-payment deep link: scroll to the calculator section
  useEffect(() => {
    if (window.location.hash === '#calculator') {
      setTimeout(() => document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' }), 400);
    }
  }, []);

  useEffect(() => {
    if (accessEmail) setResultEmail(accessEmail);
  }, [accessEmail]);

  // Calculator state - full income mapping tool
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedPaths, setSelectedPaths] = useState([]);
  const [formData, setFormData] = useState({
    monthlyExpenses: '',
    hasTargetInMind: false,
    entryPathSelected: false,
    monthlyTarget: '',
    debtAmount: '',
    debtMonths: '',
    savingsGoal: '',
    savingsMonths: '',
    weeksOff: 4,
    customWeeksOff: '',
    daysPerWeek: 5,
    federalTaxRate: 10,
    payoutFeeChoice: '',
    payoutFeeRate: 1.5,
    slowWeekBuffer: 10
  });
  const [showResults, setShowResults] = useState(false);
  const [placardColor, setPlacardColor] = useState('#2A1E5C');
  const [placardFormat, setPlacardFormat] = useState('landscape');
  const placardRef = useRef(null);

  // Scenario Compare state - persists across visits on this device
  const [scenarios, setScenarios] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('pr.relay.scenarios')) || { A: null, B: null };
    } catch {
      return { A: null, B: null };
    }
  });

  useEffect(() => {
    localStorage.setItem('pr.relay.scenarios', JSON.stringify(scenarios));
  }, [scenarios]);

  // The Relay Calculator lives inside Payday Relay, a Free Sprint or Season Pass unlocks it.
  useEffect(() => {
    const savedEmail = sessionStorage.getItem('relay_verified_email');
    if (savedEmail) setAccessEmail(savedEmail);
    if (embedded) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/user-state`, { credentials: 'include' });
        if (res.status === 401 || res.status === 403) { setAccess('locked'); return; }
        if (!res.ok) { setAccess('error'); return; }
        const s = await res.json();
        if (s.email) {
          setAccessEmail(s.email);
          sessionStorage.setItem('relay_verified_email', s.email);
        }
        const member = !!(s.payday_relay_unlocked || s.has_pit_crew || s.has_income_map_purchase);
        setAccess(member ? 'granted' : 'locked');
      } catch {
        setAccess('error');  // a network blip must never look like a paywall
      }
    })();
  }, [embedded]);

  // Prefill from Pit Crew strategy tools (Resource Library)
  useEffect(() => {
    const raw = localStorage.getItem('pr.relay.prefill');
    if (!raw) return;
    localStorage.removeItem('pr.relay.prefill');
    try {
      const { source, values } = JSON.parse(raw);
      setFormData(prev => {
        const next = { ...prev };
        if (values.debtAmount != null) next.debtAmount = String(values.debtAmount);
        if (values.debtMonths != null) next.debtMonths = String(Math.min(parseInt(values.debtMonths) || 12, 12));
        if (values.savingsGoal != null) {
          next.savingsGoal = String(values.savingsGoal);
          next.savingsMonths = '12';
        }
        if (values.weeksOff != null) {
          const w = parseInt(values.weeksOff) || 0;
          if ([0, 2, 4, 8, 12].includes(w)) {
            next.weeksOff = w;
            next.customWeeksOff = '';
          } else {
            next.weeksOff = 'custom';
            next.customWeeksOff = String(w);
          }
        }
        return next;
      });
      const names = { debt: 'Debt Strategist', pto: 'PTO Buffer' };
      toast.success(`${names[source] || 'Payday Relay'} numbers dropped in 🏁`, {
        description: 'Your result is pre-filled in the matching step of the wizard.',
      });
    } catch {
      // malformed payload, ignore
    }
  }, []);

  useEffect(() => {
    // Identity check, not a boolean flag: every real edit replaces the formData object,
    // so this survives effects being re-run on mount.
    if (initialForm.current === null) { initialForm.current = formData; return; }
    if (formData === initialForm.current) return;
    if (!midQuarterChange || showDisclaimer) return;
    try {
      if (localStorage.getItem(ackKey)) return;
    } catch { return; }
    setShowDisclaimer(true);
  }, [formData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAcceptDisclaimer = () => {
    setShowDisclaimer(false);
    try { localStorage.setItem(ackKey, 'true'); } catch { /* private mode */ }
  };

  const handleViewGuidelines = () => {
    setShowDisclaimer(true);
  };

  const accentColors = [
    { name: 'Purple', hex: '#2A1E5C' },
    { name: 'Teal', hex: '#2E86AB' },
    { name: 'Coral', hex: '#DE3C4B' },
    { name: 'Gold', hex: '#F5CB5C' },
    { name: 'Deep Teal', hex: '#114B5F' }
  ];

  // ========== CALCULATION LOGIC (identical to Income Mapping Tool) ==========

  const getWorkingWeeks = () => {
    const weeksOff = formData.weeksOff === 'custom'
      ? parseInt(formData.customWeeksOff) || 0
      : formData.weeksOff;
    return 52 - weeksOff;
  };

  const getWeeksOff = () => {
    return formData.weeksOff === 'custom'
      ? parseInt(formData.customWeeksOff) || 0
      : formData.weeksOff;
  };

  // Calculate NET weekly target (what you need to take home)
  const calculateNetWeeklyTarget = () => {
    const workWeeks = getWorkingWeeks();

    let annualNeed = 0;

    if (formData.hasTargetInMind && formData.monthlyTarget) {
      const monthlyTarget = parseFloat(formData.monthlyTarget) || 0;
      // The target IS the take-home (net). We gross it up on top with taxes.
      const monthlyNet = monthlyTarget;
      annualNeed = monthlyNet * 12;
    } else {
      const monthlyExpenses = parseFloat(formData.monthlyExpenses) || 0;
      annualNeed = monthlyExpenses * 12;
    }

    // TARGET PATH: The monthly target IS the target. Debt / savings / PTO are
    // allocations carved OUT of that take-home, not added on top. The sanity
    // check surfaces a warning if the allocations don't fit.
    //
    // EXPENSES PATH: Debt and savings are extra commitments layered on top of
    // the user's monthly expense floor, so they grow the weekly target.
    if (!formData.hasTargetInMind) {
      if (formData.debtAmount && parseFloat(formData.debtAmount) > 0 && formData.debtMonths) {
        const debtAmount = parseFloat(formData.debtAmount) || 0;
        const debtMonths = parseInt(formData.debtMonths) || 12;
        const workingWeeksInDebtPeriod = (debtMonths / 12) * workWeeks;
        const debtAnnualAddition = (debtAmount / workingWeeksInDebtPeriod) * workWeeks;
        annualNeed += debtAnnualAddition;
      }

      if (formData.savingsGoal && parseFloat(formData.savingsGoal) > 0 && formData.savingsMonths) {
        const savingsGoal = parseFloat(formData.savingsGoal) || 0;
        const savingsMonths = parseInt(formData.savingsMonths) || 12;
        const workingWeeksInSavingsPeriod = (savingsMonths / 12) * workWeeks;
        const savingsAnnualAddition = (savingsGoal / workingWeeksInSavingsPeriod) * workWeeks;
        annualNeed += savingsAnnualAddition;
      }
    }

    return annualNeed / workWeeks;
  };

  const calculateGrossWeeklyTarget = () => {
    const netWeekly = calculateNetWeeklyTarget();
    const federalTaxRate = (formData.federalTaxRate || 0) / 100;
    const totalTaxRate = SELF_EMPLOYMENT_TAX + federalTaxRate;
    return netWeekly / (1 - totalTaxRate);
  };

  const calculateRelayTarget = () => {
    const baseGross = calculateGrossWeeklyTarget();
    if (!formData.hasTargetInMind && formData.slowWeekBuffer > 0) {
      const bufferMultiplier = 1 + (formData.slowWeekBuffer / 100);
      return baseGross * bufferMultiplier;
    }
    return baseGross;
  };

  const calculateDailyTarget = () => {
    const weeklyTarget = calculateRelayTarget();
    const daysPerWeek = parseInt(formData.daysPerWeek) || 5;
    return weeklyTarget / daysPerWeek;
  };

  const getTaxBreakdown = () => {
    const grossWeekly = calculateRelayTarget();
    const federalTaxRate = (formData.federalTaxRate || 0) / 100;

    const selfEmploymentTax = grossWeekly * SELF_EMPLOYMENT_TAX;
    const federalTax = grossWeekly * federalTaxRate;
    const totalTax = selfEmploymentTax + federalTax;
    const netWeekly = grossWeekly * (1 - SELF_EMPLOYMENT_TAX - federalTaxRate);

    return { selfEmploymentTax, federalTax, totalTax, netWeekly, grossWeekly };
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const getTimeOffBuffer = () => {
    const weeksOff = getWeeksOff();
    if (weeksOff <= 0) return 0;

    const netWeeklyTarget = calculateNetWeeklyTarget();
    return (netWeeklyTarget * weeksOff) / getWorkingWeeks();
  };

  // SMART Feasibility Check
  const getFeasibilityCheck = () => {
    const workWeeks = getWorkingWeeks();

    const monthlyExpenses = formData.hasTargetInMind
      ? 0
      : (parseFloat(formData.monthlyExpenses) || 0);

    const hasExpenses = monthlyExpenses > 0;

    const grossWeekly = calculateRelayTarget();
    const federalTaxRate = (formData.federalTaxRate || 0) / 100;
    const totalTaxRate = SELF_EMPLOYMENT_TAX + federalTaxRate;
    const netWeekly = grossWeekly * (1 - totalTaxRate);
    const annualNetIncome = netWeekly * workWeeks;

    const annualExpenses = monthlyExpenses * 12;
    const debtAmount = (selectedPaths.includes('debt') && formData.debtAmount)
      ? parseFloat(formData.debtAmount) || 0
      : 0;
    const savingsAmount = (selectedPaths.includes('savings') && formData.savingsGoal)
      ? parseFloat(formData.savingsGoal) || 0
      : 0;

    const hasStretchGoals = debtAmount > 0 || savingsAmount > 0;

    const monthlyTargetNum = parseFloat(formData.monthlyTarget) || 0;
    // Target IS the take-home; allocations come out of it directly (no re-taxing).
    const targetMonthlyNet = monthlyTargetNum;
    const monthlyDebtAllocation = (debtAmount > 0 && formData.debtMonths)
      ? debtAmount / (parseInt(formData.debtMonths) || 12)
      : 0;
    const monthlySavingsAllocation = (savingsAmount > 0 && formData.savingsMonths)
      ? savingsAmount / (parseInt(formData.savingsMonths) || 12)
      : 0;
    const monthlyPtoAllocation = getWeeksOff() > 0
      ? (getTimeOffBuffer() * (52 - getWeeksOff())) / 12
      : 0;
    const monthlyGoalAllocations = monthlyDebtAllocation + monthlySavingsAllocation + monthlyPtoAllocation;
    const maxMonthlyExpenses = Math.max(0, targetMonthlyNet - monthlyGoalAllocations);

    if (!hasExpenses && formData.hasTargetInMind) {
      const goalParts = [];
      if (monthlyDebtAllocation > 0) goalParts.push(`${formatCurrency(monthlyDebtAllocation)}/mo debt`);
      if (monthlySavingsAllocation > 0) goalParts.push(`${formatCurrency(monthlySavingsAllocation)}/mo savings`);
      if (monthlyPtoAllocation > 0) goalParts.push(`${formatCurrency(monthlyPtoAllocation)}/mo time-off fund`);
      const suggestion = goalParts.length > 0
        ? `That leaves room after covering ${goalParts.join(' + ')} out of your take-home.`
        : null;
      return {
        status: 'unknown',
        hasExpenses: false,
        maxMonthlyExpenses,
        message: `To support this plan, your monthly expenses would need to stay around ${formatCurrency(maxMonthlyExpenses)} or lower, that's your ${formatCurrency(monthlyTargetNum)}/month take-home target after allocations.`,
        suggestion
      };
    }

    const workWeeksForCalc = 52 - getWeeksOff();
    const timeOffFundTotal = getTimeOffBuffer() * workWeeksForCalc;
    const totalAnnualCommitments = annualExpenses + debtAmount + savingsAmount + timeOffFundTotal;
    const surplus = annualNetIncome - totalAnnualCommitments;
    const calculatedBufferPercent = totalAnnualCommitments > 0
      ? (surplus / totalAnnualCommitments) * 100
      : 100;

    const bufferPercent = !formData.hasTargetInMind ? formData.slowWeekBuffer : calculatedBufferPercent;

    const requiredAnnualNet = totalAnnualCommitments * 1.2;
    const requiredMonthlyGross = (requiredAnnualNet / 12) / (1 - totalTaxRate);

    let status, message, suggestion;

    if (!formData.hasTargetInMind) {
      if (formData.slowWeekBuffer === 0) {
        status = 'not-realistic';
        message = `With a 0% buffer, this is your exact minimum with zero wiggle room. One slow week could throw everything off.`;
        suggestion = 'Consider adding at least a small buffer.';
      } else if (formData.slowWeekBuffer === 5) {
        status = 'tight';
        message = `With a 5% buffer, you've given yourself a tiny bit of breathing room, but a bad week could still set you back.`;
        suggestion = 'Consider increasing your buffer.';
      } else if (formData.slowWeekBuffer === 10) {
        status = 'on-track';
        message = `With a 10% buffer, you can handle the occasional slow week.`;
        suggestion = null;
      } else if (formData.slowWeekBuffer === 15) {
        status = 'solid';
        message = `With a 15% buffer, you've got real breathing room for inconsistent weeks.`;
        suggestion = null;
      } else if (formData.slowWeekBuffer === 20) {
        status = 'nice';
        message = `With a 20% buffer, you can weather a rough month without stress.`;
        suggestion = null;
      } else if (formData.slowWeekBuffer === 25) {
        status = 'great';
        message = `With a 25% buffer, you've got serious cushion for inconsistent income.`;
        suggestion = null;
      } else if (formData.slowWeekBuffer >= 30) {
        status = 'perfect';
        message = `With a 30% buffer, you're set for emergencies and slow seasons.`;
        suggestion = null;
      } else {
        status = 'on-track';
        message = `With a ${formData.slowWeekBuffer}% buffer, you've got some breathing room.`;
        suggestion = null;
      }
    }
    else if (calculatedBufferPercent >= 20) {
      status = 'on-track';
      message = `Your plan is realistic with a healthy ${Math.round(calculatedBufferPercent)}% buffer.`;
      suggestion = null;
    } else if (calculatedBufferPercent >= 0) {
      status = 'tight';
      message = `This plan works, but leaves little room for slow weeks (${Math.round(calculatedBufferPercent)}% buffer).`;
      suggestion = 'Consider extending your timeline or slightly increasing your target to build more cushion.';
    } else {
      status = 'not-realistic';
      message = `This plan currently doesn't add up. Your commitments exceed your income capacity.`;
      suggestion = `To make this work, you'd need to earn approximately ${formatCurrency(requiredMonthlyGross)}/month, or adjust your goals.`;
    }

    return {
      status,
      hasExpenses: true,
      bufferPercent: Math.round(bufferPercent),
      annualNetIncome,
      totalAnnualCommitments,
      surplus,
      requiredMonthlyGross,
      hasStretchGoals,
      message,
      suggestion
    };
  };

  // Build goal statement parts with timelines
  const getGoalParts = () => {
    const parts = [];

    if (formData.hasTargetInMind && formData.monthlyTarget) {
      parts.push(`hit my ${formatGoalCurrency(parseFloat(formData.monthlyTarget))}/month take-home target`);
    } else {
      parts.push('cover my monthly expenses');
    }

    if (formData.debtAmount && parseFloat(formData.debtAmount) > 0 && formData.debtMonths) {
      const months = parseInt(formData.debtMonths);
      const timeframe = months === 12 ? 'in 1 year' : `in ${months} months`;
      parts.push(`pay off ${formatGoalCurrency(parseFloat(formData.debtAmount))} ${timeframe}`);
    }

    if (formData.savingsGoal && parseFloat(formData.savingsGoal) > 0 && formData.savingsMonths) {
      const months = parseInt(formData.savingsMonths);
      const timeframe = months === 12 ? 'in 1 year' : `in ${months} months`;
      parts.push(`save ${formatGoalCurrency(parseFloat(formData.savingsGoal))} ${timeframe}`);
    }

    if (getWeeksOff() > 0) {
      parts.push(`take ${getWeeksOff()} weeks off this year`);
    }

    return mergeYearGoals(parts);
  };

  const getGoalStatement = () => {
    const target = calculateRelayTarget();
    const parts = getGoalParts();
    const workWeeks = getWorkingWeeks();

    if (parts.length === 1) {
      return `I need to earn ${formatGoalCurrency(target)} every week for ${workWeeks} weeks so I can ${parts[0]}.`;
    } else if (parts.length === 2) {
      return `I need to earn ${formatGoalCurrency(target)} every week for ${workWeeks} weeks so I can ${parts[0]} and ${parts[1]}.`;
    } else {
      const lastPart = parts.pop();
      return `I need to earn ${formatGoalCurrency(target)} every week for ${workWeeks} weeks so I can ${parts.join(', ')}, and ${lastPart}.`;
    }
  };

  const getGoalStatementJSX = () => {
    const target = calculateRelayTarget();
    const workWeeks = getWorkingWeeks();

    const goalItems = [];

    if (formData.hasTargetInMind && formData.monthlyTarget) {
      goalItems.push({ key: "target", year: false, node: <span>hit my <strong>{formatGoalCurrency(parseFloat(formData.monthlyTarget))}/month</strong> take-home target</span> });
    } else {
      goalItems.push({ key: "expenses", year: false, node: <span>cover my monthly expenses</span> });
    }

    if (formData.debtAmount && parseFloat(formData.debtAmount) > 0 && formData.debtMonths) {
      const months = parseInt(formData.debtMonths);
      const timeframe = months === 12 ? 'in 1 year' : `in ${months} months`;
      goalItems.push({ key: "debt", year: months === 12, node: <span>pay off <strong>{formatGoalCurrency(parseFloat(formData.debtAmount))}</strong>{months === 12 ? "" : ` ${timeframe}`}</span> });
    }

    if (formData.savingsGoal && parseFloat(formData.savingsGoal) > 0 && formData.savingsMonths) {
      const months = parseInt(formData.savingsMonths);
      const timeframe = months === 12 ? 'in 1 year' : `in ${months} months`;
      goalItems.push({ key: "savings", year: months === 12, node: <span>save <strong>{formatGoalCurrency(parseFloat(formData.savingsGoal))}</strong>{months === 12 ? "" : ` ${timeframe}`}</span> });
    }

    if (getWeeksOff() > 0) {
      goalItems.push({ key: "time", year: true, node: <span>take <strong>{getWeeksOff()} weeks</strong> off</span> });
    }

    const goalParts = mergeYearGoalNodes(goalItems);
    const renderParts = () => {
      if (goalParts.length === 1) {
        return goalParts[0];
      } else if (goalParts.length === 2) {
        return <>{goalParts[0]} and {goalParts[1]}</>;
      } else {
        const allButLast = goalParts.slice(0, -1);
        const last = goalParts[goalParts.length - 1];
        return <>{allButLast.map((part, i) => <span key={`goal-part-${i}`}>{part}{i < allButLast.length - 1 ? ', ' : ''}</span>)}, and {last}</>;
      }
    };

    return (
      <>
        I need to earn <strong>{formatGoalCurrency(target)}</strong> every week for <strong>{workWeeks} weeks</strong> so I can {renderParts()}.
      </>
    );
  };

  const getGoalStatementHTML = () => {
    const target = calculateRelayTarget();
    const workWeeks = getWorkingWeeks();

    let goalParts = [];

    if (formData.hasTargetInMind && formData.monthlyTarget) {
      goalParts.push(`hit my <b>${formatGoalCurrency(parseFloat(formData.monthlyTarget))}/month</b> take-home target`);
    } else {
      goalParts.push('cover my monthly expenses');
    }

    if (formData.debtAmount && parseFloat(formData.debtAmount) > 0 && formData.debtMonths) {
      const months = parseInt(formData.debtMonths);
      const timeframe = months === 12 ? 'in 1 year' : `in ${months} months`;
      goalParts.push(`pay off <b>${formatGoalCurrency(parseFloat(formData.debtAmount))}</b> ${timeframe}`);
    }

    if (formData.savingsGoal && parseFloat(formData.savingsGoal) > 0 && formData.savingsMonths) {
      const months = parseInt(formData.savingsMonths);
      const timeframe = months === 12 ? 'in 1 year' : `in ${months} months`;
      goalParts.push(`save <b>${formatGoalCurrency(parseFloat(formData.savingsGoal))}</b> ${timeframe}`);
    }

    if (getWeeksOff() > 0) {
      goalParts.push(`take <b>${getWeeksOff()} weeks</b> off this year`);
    }

    goalParts = mergeYearGoals(goalParts);

    let partsString;
    if (goalParts.length === 1) {
      partsString = goalParts[0];
    } else if (goalParts.length === 2) {
      partsString = `${goalParts[0]} and ${goalParts[1]}`;
    } else {
      const lastPart = goalParts.pop();
      partsString = `${goalParts.join(', ')}, and ${lastPart}`;
    }

    return `I need to earn <b>${formatGoalCurrency(target)}</b> every week for <b>${workWeeks} weeks</b> so I can ${partsString}.`;
  };

  // ========== TIME-PHASED TIMELINE LOGIC ==========

  const getGoalTimelines = () => {
    const timelines = [];

    if (formData.debtAmount && parseFloat(formData.debtAmount) > 0 && formData.debtMonths) {
      const debtAmount = parseFloat(formData.debtAmount) || 0;
      const debtMonths = parseInt(formData.debtMonths) || 12;
      const workWeeks = getWorkingWeeks();
      const workingWeeksInDebtPeriod = (debtMonths / 12) * workWeeks;
      const weeklyAmount = debtAmount / workingWeeksInDebtPeriod;

      timelines.push({
        id: 'debt',
        name: 'Debt Payoff',
        endMonth: debtMonths,
        weeklyAmount: weeklyAmount,
        totalAmount: debtAmount,
        color: '#DE3C4B',
        icon: 'CreditCard'
      });
    }

    if (formData.savingsGoal && parseFloat(formData.savingsGoal) > 0 && formData.savingsMonths) {
      const savingsGoal = parseFloat(formData.savingsGoal) || 0;
      const savingsMonths = parseInt(formData.savingsMonths) || 12;
      const workWeeks = getWorkingWeeks();
      const workingWeeksInSavingsPeriod = (savingsMonths / 12) * workWeeks;
      const weeklyAmount = savingsGoal / workingWeeksInSavingsPeriod;

      timelines.push({
        id: 'savings',
        name: 'Savings Goal',
        endMonth: savingsMonths,
        weeklyAmount: weeklyAmount,
        totalAmount: savingsGoal,
        color: '#2E86AB',
        icon: 'PiggyBank'
      });
    }

    return timelines.sort((a, b) => a.endMonth - b.endMonth);
  };

  const generatePhases = () => {
    const timelines = getGoalTimelines();
    if (timelines.length === 0) return [];

    const daysPerWeek = parseInt(formData.daysPerWeek) || 5;
    const federalTaxRate = (formData.federalTaxRate || 0) / 100;
    const totalTaxRate = SELF_EMPLOYMENT_TAX + federalTaxRate;

    if (formData.hasTargetInMind && formData.monthlyTarget) {
      const endMonths = [...new Set(timelines.map(t => Math.min(t.endMonth, 12)))].sort((a, b) => a - b);

      const phases = [];
      let startMonth = 1;
      let activeGoals = [...timelines];

      const phase1GrossWeekly = calculateRelayTarget();
      const phase1GrossDaily = calculateDailyTarget();
      const phase1NetWeekly = phase1GrossWeekly * (1 - totalTaxRate);

      endMonths.forEach((endMonth, index) => {
        let grossWeekly, grossDaily, netWeekly;

        if (index === 0) {
          grossWeekly = phase1GrossWeekly;
          grossDaily = phase1GrossDaily;
          netWeekly = phase1NetWeekly;
        } else {
          const completedGoalsAmount = timelines
            .filter(g => g.endMonth <= endMonths[index - 1])
            .reduce((sum, g) => sum + g.weeklyAmount, 0);
          netWeekly = phase1NetWeekly - completedGoalsAmount * (1 - totalTaxRate);
          grossWeekly = netWeekly / (1 - totalTaxRate);
          grossDaily = grossWeekly / daysPerWeek;
        }

        const completingGoals = activeGoals.filter(g => g.endMonth === endMonth);

        phases.push({
          phaseNumber: index + 1,
          startMonth,
          endMonth,
          netWeekly,
          grossWeekly,
          grossDaily,
          activeGoals: [...activeGoals],
          completingGoals,
          isLastPhase: index === endMonths.length - 1
        });

        activeGoals = activeGoals.filter(g => g.endMonth > endMonth);
        startMonth = endMonth + 1;
      });

      return phases;
    }

    const endMonths = [...new Set(timelines.map(t => Math.min(t.endMonth, 12)))].sort((a, b) => a - b);

    const phases = [];
    let startMonth = 1;
    let activeGoals = [...timelines];

    const phase1GrossWeekly = calculateRelayTarget();
    const phase1GrossDaily = calculateDailyTarget();
    const phase1NetWeekly = phase1GrossWeekly * (1 - totalTaxRate);

    endMonths.forEach((endMonth, index) => {
      let grossWeekly, grossDaily, netWeekly;

      if (index === 0) {
        grossWeekly = phase1GrossWeekly;
        grossDaily = phase1GrossDaily;
        netWeekly = phase1NetWeekly;
      } else {
        const completedGoalsAmount = timelines
          .filter(g => g.endMonth <= endMonths[index - 1])
          .reduce((sum, g) => sum + g.weeklyAmount, 0);
        netWeekly = phase1NetWeekly - completedGoalsAmount;
        grossWeekly = netWeekly / (1 - totalTaxRate);
        grossDaily = grossWeekly / daysPerWeek;
      }

      const completingGoals = activeGoals.filter(g => g.endMonth === endMonth);

      phases.push({
        phaseNumber: index + 1,
        startMonth,
        endMonth,
        netWeekly,
        grossWeekly,
        grossDaily,
        activeGoals: [...activeGoals],
        completingGoals,
        isLastPhase: index === endMonths.length - 1
      });

      activeGoals = activeGoals.filter(g => g.endMonth > endMonth);
      startMonth = endMonth + 1;
    });

    return phases;
  };

  const calculateMomentumOptions = () => {
    const phases = generatePhases().filter(p => !p.isOngoing);
    if (phases.length < 2) return null;

    const phase1 = phases[0];
    const phase2 = phases[1];
    const workWeeks = getWorkingWeeks();
    const daysPerWeek = parseInt(formData.daysPerWeek) || 5;
    const weeksPerMonth = workWeeks / 12;
    const federalTaxRate = (formData.federalTaxRate || 0) / 100;
    const totalTaxRate = SELF_EMPLOYMENT_TAX + federalTaxRate;

    const monthsInPhase2 = 12 - phase1.endMonth;
    const weeksInPhase2 = monthsInPhase2 * weeksPerMonth;

    const savingsGoal = parseFloat(formData.savingsGoal) || 0;

    const savingsGoalFromPhase1 = phase1.activeGoals?.find(g => g.id === 'savings');
    const savingsFromPhase1 = savingsGoalFromPhase1 ? savingsGoalFromPhase1.weeklyAmount * (phase1.endMonth * weeksPerMonth) : 0;

    const extraPerWeekOptionA = phase1.netWeekly - phase2.netWeekly;
    const extraSavingsByYearEnd = extraPerWeekOptionA * weeksInPhase2;
    const totalSavedOptionA = savingsGoal + extraSavingsByYearEnd;

    const totalSavedOptionC = savingsGoal;

    const reducedDays = Math.max(daysPerWeek - 1, 3);
    const flexWeeklyAtReducedDays = phase1.grossDaily * reducedDays;
    const flexNetWeeklyAtReducedDays = flexWeeklyAtReducedDays * (1 - totalTaxRate);
    const baseExpensesWeekly = (parseFloat(formData.monthlyExpenses) * 12) / workWeeks;
    const flexSavingsWeeklyAtReducedDays = flexNetWeeklyAtReducedDays - baseExpensesWeekly;
    const flexTotalSavingsPhase2 = flexSavingsWeeklyAtReducedDays > 0 ? flexSavingsWeeklyAtReducedDays * weeksInPhase2 : 0;
    const totalSavedIfDayOff = savingsFromPhase1 + flexTotalSavingsPhase2;

    const canTakeDayOff = totalSavedIfDayOff >= savingsGoal && daysPerWeek > 3;

    const midpointWeekly = (phase1.grossWeekly + phase2.grossWeekly) / 2;
    const midpointDaily = midpointWeekly / daysPerWeek;
    const midpointNetWeekly = midpointWeekly * (1 - totalTaxRate);
    const midpointSavingsWeekly = midpointNetWeekly - baseExpensesWeekly;
    const totalSavedAtMidpoint = savingsFromPhase1 + midpointSavingsWeekly * weeksInPhase2;

    let flexibilityOption;
    if (canTakeDayOff) {
      flexibilityOption = {
        type: 'day-off',
        dailyTarget: phase1.grossDaily,
        daysPerWeek: reducedDays,
        weeklyTarget: flexWeeklyAtReducedDays,
        totalSavedByYearEnd: totalSavedIfDayOff,
        description: `Same daily rate, ${daysPerWeek - reducedDays} day off`
      };
    } else {
      flexibilityOption = {
        type: 'lower-daily',
        dailyTarget: midpointDaily,
        daysPerWeek: daysPerWeek,
        weeklyTarget: midpointWeekly,
        totalSavedByYearEnd: totalSavedAtMidpoint,
        description: 'Lower daily target, same days'
      };
    }

    const netWeeklyNeed = calculateNetWeeklyTarget();
    const extraWeeksOffFunded = netWeeklyNeed > 0 ? extraSavingsByYearEnd / netWeeklyNeed : 0;

    return {
      phase1,
      phase2,
      monthsInPhase2,
      weeksInPhase2,
      savingsGoal,
      daysPerWeek,
      optionA: {
        name: "Keep Your Momentum",
        weeklyTarget: phase1.grossWeekly,
        dailyTarget: phase1.grossDaily,
        daysPerWeek: daysPerWeek,
        extraSavings: extraSavingsByYearEnd,
        totalSavedByYearEnd: totalSavedOptionA,
        extraPerWeek: extraPerWeekOptionA
      },
      optionB: {
        name: "Flexibility",
        weeklyTargetHigh: phase1.grossWeekly,
        weeklyTargetLow: phase2.grossWeekly,
        dailyTargetHigh: phase1.grossDaily,
        dailyTargetLow: phase2.grossDaily,
        totalSavedHigh: totalSavedOptionA,
        totalSavedLow: savingsGoal,
        extraSavingsHigh: extraSavingsByYearEnd,
        daysPerWeek: daysPerWeek
      },
      optionC: {
        name: "Cover Your Expenses",
        weeklyTarget: phase2.grossWeekly,
        dailyTarget: phase2.grossDaily,
        daysPerWeek: daysPerWeek,
        totalSavedByYearEnd: totalSavedOptionC
      },
      optionD: {
        name: "Buy Back Time",
        weeklyTarget: phase1.grossWeekly,
        dailyTarget: phase1.grossDaily,
        daysPerWeek: daysPerWeek,
        extraPerWeek: extraPerWeekOptionA,
        ptoFundByYearEnd: extraSavingsByYearEnd,
        extraWeeksOff: extraWeeksOffFunded,
        totalSavedByYearEnd: savingsGoal
      }
    };
  };

  const getTrajectoryStory = () => {
    const hasDebt = formData.debtAmount && parseFloat(formData.debtAmount) > 0;
    const hasSavings = formData.savingsGoal && parseFloat(formData.savingsGoal) > 0;

    if (hasDebt && hasSavings) return 'debt-and-savings';
    if (hasDebt && !hasSavings) return 'debt-only';
    if (!hasDebt && hasSavings) return 'savings-only';

    if (formData.hasTargetInMind && formData.monthlyTarget) {
      return 'target-mode';
    }

    return 'expenses-only';
  };

  // ========== WIZARD FLOW ==========

  const getSteps = () => {
    if (formData.hasTargetInMind) {
      return ['expenses', 'debt', 'savings', 'weeks', 'days', 'taxes'];
    }
    return ['expenses', 'debt', 'savings', 'weeks', 'days', 'taxes', 'buffer'];
  };

  const steps = getSteps();
  const currentStepId = steps[currentStep];

  const canProceed = () => {
    switch (currentStepId) {
      case 'expenses':
        if (!formData.entryPathSelected) return false;
        if (formData.hasTargetInMind) {
          return formData.monthlyTarget && parseFloat(formData.monthlyTarget) > 0;
        }
        return formData.monthlyExpenses && parseFloat(formData.monthlyExpenses) > 0;
      case 'debt':
        if (!formData.debtAmount || parseFloat(formData.debtAmount) === 0) return true;
        return formData.debtMonths && parseInt(formData.debtMonths) > 0;
      case 'savings':
        return true;
      case 'weeks':
        if (formData.weeksOff === 'custom') {
          const customWeeksOff = parseInt(formData.customWeeksOff);
          return customWeeksOff >= 0 && customWeeksOff <= 51;
        }
        return true;
      case 'days':
        return formData.daysPerWeek && parseInt(formData.daysPerWeek) > 0 && parseInt(formData.daysPerWeek) <= 7;
      case 'taxes':
        return true;
      case 'buffer':
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (currentStep === steps.length - 1) {
      const paths = ['income'];
      if (formData.debtAmount && parseFloat(formData.debtAmount) > 0) {
        paths.push('debt');
      }
      if (formData.savingsGoal && parseFloat(formData.savingsGoal) > 0) {
        paths.push('savings');
      }
      setSelectedPaths(paths);
      setShowResults(true);
      setTimeout(() => {
        const resultsEl = document.getElementById('results-section');
        if (resultsEl) {
          const yOffset = -120;
          const y = resultsEl.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const resetCalculator = () => {
    setCurrentStep(0);
    setSelectedPaths([]);
    setFormData({
      monthlyExpenses: '',
      hasTargetInMind: false,
      entryPathSelected: false,
      monthlyTarget: '',
      debtAmount: '',
      debtMonths: '',
      savingsGoal: '',
      savingsMonths: '',
      weeksOff: 4,
      customWeeksOff: '',
      daysPerWeek: 5,
      federalTaxRate: 10,
      payoutFeeChoice: '',
      payoutFeeRate: 1.5,
      slowWeekBuffer: 10
    });
    setShowResults(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ========== EXPORT (goal card) ==========

  const downloadPNG = async () => {
    if (!placardRef.current) return;

    try {
      toast.loading('Creating your image...');
      const canvas = await html2canvas(placardRef.current, {
        scale: 2,
        backgroundColor: '#FDF8F3',
        logging: false
      });

      const link = document.createElement('a');
      link.download = `my-income-target-${placardFormat}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      toast.dismiss();
      toast.success('Image saved!');
    } catch (error) {
      toast.dismiss();
      toast.error('Something went wrong. Try again.');
    }
  };

  const downloadPDF = async () => {
    if (!placardRef.current) return;

    try {
      toast.loading('Creating your goal card...');
      const canvas = await html2canvas(placardRef.current, {
        scale: 2,
        backgroundColor: '#FDF8F3',
        logging: false
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: placardFormat === 'landscape' ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width / 2, canvas.height / 2]
      });

      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 2, canvas.height / 2);
      pdf.save(`my-income-target-${placardFormat}-${Date.now()}.pdf`);
      toast.dismiss();
      toast.success('Your goal card is ready!');
    } catch (error) {
      toast.dismiss();
      toast.error('Something went wrong. Try again.');
    }
  };

  // ========== SCENARIO COMPARE ==========

  const buildResultsPayload = (email) => {
    const taxRate = parseFloat(formData.federalTaxRate) || 0;
    const grossWeekly = calculateRelayTarget();
    const netWeekly = grossWeekly * (1 - (SELF_EMPLOYMENT_TAX + taxRate / 100));
    const feasibility = getFeasibilityCheck();
    return {
      email,
      weekly_target: grossWeekly,
      daily_target: calculateDailyTarget(),
      days_per_week: parseInt(formData.daysPerWeek) || 5,
      working_weeks: getWorkingWeeks(),
      goal_statement: getGoalStatement(),
      trajectory_type: getTrajectoryStory(),
      debt_amount: parseFloat(formData.debtAmount) || null,
      debt_months: parseInt(formData.debtMonths) || null,
      savings_goal: parseFloat(formData.savingsGoal) || null,
      savings_months: parseInt(formData.savingsMonths) || null,
      monthly_expenses: parseFloat(formData.monthlyExpenses) || 0,
      time_off_buffer: getTimeOffBuffer(),
      weeks_off: getWeeksOff(),
      federal_tax_rate: taxRate,
      net_weekly: netWeekly,
      monthly_target: formData.hasTargetInMind ? parseFloat(formData.monthlyTarget) : null,
      gross_annual_income: grossWeekly * getWorkingWeeks(),
      feasibility_status: feasibility.status,
      feasibility_message: feasibility.message,
      max_monthly_expenses: feasibility.maxMonthlyExpenses || null,
      has_target_in_mind: formData.hasTargetInMind,
      trajectory_options: calculateMomentumOptions(),
      // Optional estimated payout cost -> LEGEND "Other" fee rate (E51/E32).
      // Three states: untouched -> null (preserve existing cell); explicit "No" -> 0;
      // "Yes" -> the exact percentage the member typed.
      payout_fee_rate: formData.payoutFeeChoice === 'yes'
        ? (parseFloat(formData.payoutFeeRate) || 0)
        : formData.payoutFeeChoice === 'no'
          ? 0
          : null
    };
  };

  const handleEmailResults = async (e) => {
    e.preventDefault();
    if (!resultEmail.trim() || emailSending) return;
    setEmailSending(true);
    try {
      const response = await fetch(`${API_URL}/api/email/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildResultsPayload(resultEmail.toLowerCase()))
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Could not send your results, try again.');
        return;
      }

      setEmailSent(true);
      toast.success(`Results sent to ${resultEmail}!`);
    } catch (error) {
      toast.error('Network hiccup, try again.');
    } finally {
      setEmailSending(false);
    }
  };

  const handleSeasonSetupSave = async () => {
    if (seasonSaving) return;
    setSeasonSaving(true);
    try {
      const payload = buildResultsPayload('member');
      delete payload.email;
      const res = await fetch(`${API_URL}/api/caddy/season-setup/recalibrate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        const detail = errorData.detail;
        toast.error(typeof detail === 'string' ? detail : 'Could not save your plan, try again.');
        return;
      }
      window.location.assign('/app?recalibrated=1');
    } catch (error) {
      toast.error('Network hiccup, try again.');
    } finally {
      setSeasonSaving(false);
    }
  };

  // Unified funnel Step 1 → 2: save the plan to the signed-in account, then go sync the sheet
  const handleOnboardingLock = async () => {
    if (lockSaving) return;
    setLockSaving(true);
    try {
      const payload = buildResultsPayload('member');
      delete payload.email;
      const res = await fetch(`${API_URL}/api/caddy/onboarding/lock-plan`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        const detail = errorData.detail;
        toast.error(typeof detail === 'string' ? detail : 'Could not save your numbers, try again.');
        return;
      }
      const data = await res.json();
      if (data.sheet_built || !data.setup_url) {
        window.location.assign('/dashboard');
      } else {
        window.location.assign(data.setup_url);
      }
    } catch (error) {
      toast.error('Network hiccup, try again.');
    } finally {
      setLockSaving(false);
    }
  };

  const buildScenarioSnapshot = () => {
    const feasibility = getFeasibilityCheck();
    return {
      savedAt: Date.now(),
      formData: { ...formData },
      weeklyGross: calculateRelayTarget(),
      dailyTarget: calculateDailyTarget(),
      netWeekly: getTaxBreakdown().netWeekly,
      workingWeeks: getWorkingWeeks(),
      weeksOff: getWeeksOff(),
      ptoWeekly: getTimeOffBuffer(),
      feasibilityStatus: feasibility.status
    };
  };

  const handleSaveScenario = (slot) => {
    setScenarios(prev => ({ ...prev, [slot]: buildScenarioSnapshot() }));
    toast.success(`Saved as Scenario ${slot}!`);
  };

  const handleLoadScenario = (slot) => {
    const s = scenarios[slot];
    if (!s) return;
    setFormData({ ...s.formData });
    const paths = ['income'];
    if (s.formData.debtAmount && parseFloat(s.formData.debtAmount) > 0) paths.push('debt');
    if (s.formData.savingsGoal && parseFloat(s.formData.savingsGoal) > 0) paths.push('savings');
    setSelectedPaths(paths);
    setCurrentStep(0);
    setShowResults(true);
    toast.success(`Scenario ${slot} loaded`);
    setTimeout(() => {
      const el = document.getElementById('results-section');
      if (el) {
        window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 120, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleClearScenario = (slot) => {
    setScenarios(prev => ({ ...prev, [slot]: null }));
    toast.success(`Scenario ${slot} cleared`);
  };

  // ========== DISCLAIMER MODAL ==========

  // ========== ACCESS GATE, Sprint or Season Pass required ==========

  if (!embedded && access !== 'granted') {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: '#FDFBF7' }}>
        <header className="py-4 px-6" style={{ borderBottom: '1px solid rgba(42, 30, 92, 0.1)' }}>
          <div className="max-w-4xl mx-auto">
            <Link to="/" className="font-heading text-lg font-medium" style={{ color: '#2A1E5C' }}>
              paydayrelay.com
            </Link>
          </div>
        </header>
        <CalculatorGate checking={access === 'checking'} error={access === 'error'} />
        <SiteFooter />
      </div>
    );
  }

  // Pit Crew tools. Scratchpads that hand their answer to the step you're on.
  const ToolLink = ({ tool, label }) => (
    <button
      onClick={() => setActiveTool(tool)}
      className="w-full py-3 font-body text-sm underline transition-opacity hover:opacity-70"
      style={{ color: '#2A1E5C' }}
      data-testid={`open-tool-${tool}`}
    >
      {label}
    </button>
  );

  const renderActiveTool = () => {
    if (!activeTool) return null;
    const close = () => setActiveTool(null);
    const apply = (patch) => { setFormData({ ...formData, ...patch }); setActiveTool(null); };
    if (activeTool === 'debt') {
      return <DebtStrategistModal onApply={apply} onClose={close} formatCurrency={formatCurrency} />;
    }
    if (activeTool === 'savings') {
      return (
        <SavingsSplitterModal
          onApply={apply}
          onClose={close}
          formatCurrency={formatCurrency}
          workWeeks={getWorkingWeeks()}
          daysPerWeek={formData.daysPerWeek || 5}
        />
      );
    }
    return (
      <PtoBufferModal
        onApply={apply}
        onClose={close}
        formatCurrency={formatCurrency}
        suggestedWeeklyPay={calculateNetWeeklyTarget()}
      />
    );
  };

  if (showDisclaimer) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6" style={{ background: 'rgba(42, 30, 92, 0.6)' }} data-testid="midquarter-disclaimer">
        <div
          className="relative w-full max-w-5xl max-h-[94vh] overflow-y-auto"
          style={{ background: '#FAF7F0', border: '1px solid #E0D9CB', borderRadius: 0, boxShadow: '0 24px 60px rgba(42, 30, 92, 0.2)' }}
        >
          <div className="px-6 sm:px-10 py-4 flex items-center justify-between" style={{ background: '#2A1E5C' }}>
            <p className="font-body text-[11px] uppercase tracking-[0.26em]" style={{ color: 'rgba(255,255,255,0.72)' }}>
              A quick heads up before you change your numbers
            </p>
            <button
              onClick={() => setShowDisclaimer(false)}
              className="p-1 transition-opacity hover:opacity-60"
              style={{ color: 'rgba(255,255,255,0.72)' }}
              aria-label="Close"
              data-testid="disclaimer-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="px-6 sm:px-10 py-8 sm:py-10">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-3" style={{ color: '#2A1E5C' }}>
              You're part-way through a quarter
            </h2>
            <p className="font-body text-sm sm:text-[15px] leading-relaxed mb-8 max-w-3xl" style={{ color: '#5D5A6B' }}>
              This calculator is a planning tool, so nothing here touches your race until you choose to lock
              it in. Here is what that means while a quarter is already running.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="p-6" style={{ background: '#FFFFFF', border: '1px solid #E8E2D5' }}>
                <p className="font-body text-[11px] uppercase tracking-[0.2em] mb-3" style={{ color: '#8B7E6A' }}>
                  Safe to explore
                </p>
                <p className="font-body text-sm leading-relaxed mb-3" style={{ color: '#2A1E5C' }}>
                  Try any numbers you like. Different expenses, a shorter debt timeline, a bigger savings
                  goal, fewer days a week. Your target, your sheet and your progress stay exactly as they are.
                </p>
                <p className="font-body text-sm leading-relaxed" style={{ color: '#5D5A6B' }}>
                  Mid-quarter, enter what is <strong style={{ color: '#2A1E5C', fontWeight: 600 }}>left</strong>:
                  your remaining debt balance, your remaining savings goal, and the months you have left in
                  the year. Otherwise the target comes back higher than it needs to be.
                </p>
              </div>

              <div className="p-6" style={{ background: '#FFFFFF', border: '1px solid #E8E2D5' }}>
                <p className="font-body text-[11px] uppercase tracking-[0.2em] mb-3" style={{ color: '#8B7E6A' }}>
                  Before you lock anything in
                </p>
                <p className="font-body text-sm leading-relaxed mb-3" style={{ color: '#2A1E5C' }}>
                  A new target applies to your whole race, not just from today. Your progress bar and the
                  weeks you have already run get measured against the new number.
                </p>
                <p className="font-body text-sm leading-relaxed" style={{ color: '#5D5A6B' }}>
                  If you want a clean recalibration, your next quarterly tax day at the start of the coming quarter is
                  built for exactly that, and it writes the new numbers to your sheet for you.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-5">
              <button
                onClick={handleAcceptDisclaimer}
                className="font-body text-sm font-semibold px-9 py-3.5 transition-opacity hover:opacity-90"
                style={{ background: '#2A1E5C', color: '#F2EFE7', borderRadius: 0 }}
                data-testid="disclaimer-accept-btn"
              >
                Continue to the calculator
              </button>
              <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>
                You will only see this once this quarter.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========== MAIN CALCULATOR UI ==========

  return (
    <div className="min-h-screen" style={{ background: '#FDFBF7' }}>
      {/* Header */}
      {!embedded && (
        <header className="py-4 px-6" style={{ borderBottom: '1px solid rgba(42, 30, 92, 0.1)' }}>
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <Link to="/" className="font-heading text-lg font-medium" style={{ color: '#2A1E5C' }}>
              paydayrelay.com
            </Link>
            <div className="flex items-center gap-4">
              <button
                onClick={handleViewGuidelines}
                className="font-body text-sm underline hover:opacity-70"
                style={{ color: '#5D5A6B' }}
                data-testid="view-guidelines-btn"
              >
                View Guidelines
              </button>
              {accessEmail && (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" style={{ color: '#4CAF50' }} />
                  <span className="font-body text-sm" style={{ color: '#5D5A6B' }}>{accessEmail}</span>
                </div>
              )}
            </div>
          </div>
        </header>
      )}

      {/* Compact reminder banner */}
      <div className="px-6 py-3" style={{ background: 'rgba(46, 134, 171, 0.1)' }}>
        <div className="max-w-4xl mx-auto">
          <p className="font-body text-sm text-center" style={{ color: '#5D5A6B' }}>
            <strong style={{ color: '#2A1E5C' }}>Scenario Testing:</strong> Use this to plan and recalibrate. Stay committed to your yearly goals.
          </p>
        </div>
      </div>

      {/* Calculator */}
      <main id="calculator" className="py-12 px-4 sm:px-6 lg:px-8" style={{ scrollMarginTop: '80px' }}>
        {renderActiveTool()}
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl font-bold mb-2" style={{ color: '#2A1E5C' }}>
              Relay Calculator
            </h1>
            <p className="font-body text-lg" style={{ color: '#5D5A6B' }}>
              Map your weekly income target, taxes, PTO, and goals in a few minutes
            </p>
          </div>

          {!showResults ? (
            <div className="card-organic p-8 lg:p-12 shadow-dreamy" data-testid="calculator">
              {/* Progress */}
              <div className="mb-10">
                <div className="flex justify-between mb-4">
                  {steps.map((step, idx) => (
                    <div
                      key={step}
                      className={`w-3 h-3 rounded-full transition-all ${
                        idx < currentStep ? 'bg-teal-500' :
                        idx === currentStep ? 'bg-yellow-400 scale-125 shadow-lg' :
                        'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${(currentStep / (steps.length - 1)) * 100}%`,
                      background: 'linear-gradient(90deg, #F5CB5C, #2E86AB)'
                    }}
                  />
                </div>
              </div>

              {/* Step Content */}
              <div className="min-h-[400px] flex flex-col">

                {/* Step: Entry Path Selection & Input */}
                {currentStepId === 'expenses' && (
                  <div className="flex-1 flex flex-col items-center justify-center animate-fade-up-soft">

                    {!formData.entryPathSelected ? (
                      <>
                        <div className="w-20 h-20 blob-1 flex items-center justify-center mb-6" style={{ background: 'rgba(245, 203, 92, 0.15)' }}>
                          <Target className="w-10 h-10" style={{ color: '#F5CB5C' }} />
                        </div>
                        <h3 className="font-heading text-2xl font-medium mb-2 text-center" style={{ color: '#2A1E5C' }}>
                          How do you want to calculate your weekly income target?
                        </h3>
                        <p className="font-body text-sm mb-8 text-center max-w-md" style={{ color: '#8B7E6A' }}>
                          Choose the starting point that fits your situation.
                        </p>

                        <div className="w-full max-w-lg space-y-4">
                          {/* Option 1: Target First */}
                          <button
                            onClick={() => setFormData({ ...formData, hasTargetInMind: true, entryPathSelected: true })}
                            className="w-full p-5 rounded-xl text-left transition-all hover:scale-[1.02] group"
                            style={{
                              background: 'rgba(245, 203, 92, 0.08)',
                              border: '2px solid rgba(245, 203, 92, 0.5)'
                            }}
                            data-testid="path-target"
                          >
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(245, 203, 92, 0.2)' }}>
                                <Target className="w-6 h-6" style={{ color: '#F5CB5C' }} />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-heading text-lg font-medium mb-1" style={{ color: '#2A1E5C' }}>
                                  Start with a monthly take-home target
                                </h4>
                                <p className="font-body text-sm mb-2" style={{ color: '#8B7E6A' }}>
                                  I already know how much I want to take home each month.
                                </p>
                                <p className="font-body text-xs italic mb-3" style={{ color: '#2A1E5C' }}>
                                  Best if you're aiming high or have a specific goal in mind.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  <span className="px-2 py-1 rounded-full font-body text-xs" style={{ background: 'rgba(245, 203, 92, 0.15)', color: '#8B7E6A' }}>Visa requirements</span>
                                  <span className="px-2 py-1 rounded-full font-body text-xs" style={{ background: 'rgba(245, 203, 92, 0.15)', color: '#8B7E6A' }}>Rent qualification</span>
                                  <span className="px-2 py-1 rounded-full font-body text-xs" style={{ background: 'rgba(245, 203, 92, 0.15)', color: '#8B7E6A' }}>Business goals</span>
                                </div>
                              </div>
                              <ChevronRight className="w-5 h-5 flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" style={{ color: '#F5CB5C' }} />
                            </div>
                          </button>

                          {/* Option 2: Expenses First */}
                          <button
                            onClick={() => setFormData({ ...formData, hasTargetInMind: false, entryPathSelected: true })}
                            className="w-full p-5 rounded-xl text-left transition-all hover:scale-[1.02] group"
                            style={{
                              background: 'rgba(46, 134, 171, 0.08)',
                              border: '2px solid rgba(46, 134, 171, 0.5)'
                            }}
                            data-testid="path-expenses"
                          >
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(46, 134, 171, 0.2)' }}>
                                <DollarSign className="w-6 h-6" style={{ color: '#2E86AB' }} />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-heading text-lg font-medium mb-1" style={{ color: '#2A1E5C' }}>
                                  Start with my monthly expenses
                                </h4>
                                <p className="font-body text-sm mb-2" style={{ color: '#8B7E6A' }}>
                                  Calculate the income I need based on my bills and goals.
                                </p>
                                <p className="font-body text-xs italic mb-3" style={{ color: '#2A1E5C' }}>
                                  Best if you want to know the minimum you need to cover your goals.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  <span className="px-2 py-1 rounded-full font-body text-xs" style={{ background: 'rgba(46, 134, 171, 0.15)', color: '#8B7E6A' }}>Rent & bills</span>
                                  <span className="px-2 py-1 rounded-full font-body text-xs" style={{ background: 'rgba(46, 134, 171, 0.15)', color: '#8B7E6A' }}>Debt payoff</span>
                                  <span className="px-2 py-1 rounded-full font-body text-xs" style={{ background: 'rgba(46, 134, 171, 0.15)', color: '#8B7E6A' }}>Savings goals</span>
                                </div>
                              </div>
                              <ChevronRight className="w-5 h-5 flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" style={{ color: '#2E86AB' }} />
                            </div>
                          </button>
                        </div>
                      </>
                    ) : formData.hasTargetInMind ? (
                      /* Target Input Mode */
                      <>
                        <div className="w-20 h-20 blob-1 flex items-center justify-center mb-6" style={{ background: 'rgba(245, 203, 92, 0.15)' }}>
                          <Target className="w-10 h-10" style={{ color: '#F5CB5C' }} />
                        </div>
                        <h3 className="font-heading text-2xl font-medium mb-2 text-center" style={{ color: '#2A1E5C' }}>
                          How much do you want to take home each month?
                        </h3>
                        <p className="font-body text-sm mb-6 text-center max-w-md" style={{ color: '#8B7E6A' }}>
                          Enter the take-home amount you want <strong>after taxes</strong>. We'll gross it up and translate it into your weekly target.
                        </p>
                        <div className="w-full max-w-xs mb-6 flex items-center justify-center gap-2">
                          <span className="font-heading text-2xl" style={{ color: '#C4B8A8' }}>$</span>
                          <input
                            type="number"
                            value={formData.monthlyTarget}
                            onChange={(e) => setFormData({ ...formData, monthlyTarget: e.target.value })}
                            placeholder="5,000"
                            step="500"
                            min="0"
                            className="w-40 font-heading text-3xl text-center py-4 border-b-3 bg-transparent focus:outline-none transition"
                            style={{ color: '#2A1E5C', borderBottomWidth: '3px', borderColor: '#E4DCD2' }}
                            data-testid="target-input"
                            autoFocus
                          />
                          <span className="font-body text-sm" style={{ color: '#A89F8F' }}>/month</span>
                        </div>

                        {/* Examples */}
                        <div className="w-full max-w-sm p-4 rounded-xl mb-5" style={{ background: 'rgba(245, 203, 92, 0.08)' }}>
                          <p className="font-body text-xs font-medium mb-2" style={{ color: '#8B7E6A' }}>
                            Common reasons for a specific target:
                          </p>
                          <ul className="font-body text-xs space-y-1" style={{ color: '#A89F8F' }}>
                            <li>• Proof of income for apartment rental</li>
                            <li>• Residency requirements abroad</li>
                            <li>• Qualifying for a mortgage or loan</li>
                            <li>• Paying off a tax bill this year</li>
                            <li>• Meeting child support obligations</li>
                          </ul>
                        </div>

                        {/* Switch path */}
                        <button
                          onClick={() => setFormData({ ...formData, hasTargetInMind: false, monthlyTarget: '', entryPathSelected: false })}
                          className="font-body text-sm underline decoration-dotted underline-offset-4 hover:opacity-80 transition"
                          style={{ color: '#8B7E6A' }}
                          data-testid="switch-path"
                        >
                          ← Choose a different starting point
                        </button>
                      </>
                    ) : (
                      /* Expenses Input Mode */
                      <>
                        <div className="w-20 h-20 blob-1 flex items-center justify-center mb-6" style={{ background: 'rgba(46, 134, 171, 0.15)' }}>
                          <DollarSign className="w-10 h-10" style={{ color: '#2E86AB' }} />
                        </div>
                        <h3 className="font-heading text-2xl font-medium mb-2 text-center" style={{ color: '#2A1E5C' }}>
                          What are your monthly expenses?
                        </h3>
                        <p className="font-body text-sm mb-8 text-center max-w-md" style={{ color: '#8B7E6A' }}>
                          Rent, bills, food, subscriptions. Everything you need to cover each month.
                          An estimate is fine. This is your baseline.
                        </p>
                        <div className="w-full max-w-xs flex items-center justify-center gap-2">
                          <span className="font-heading text-2xl" style={{ color: '#C4B8A8' }}>$</span>
                          <input
                            type="number"
                            value={formData.monthlyExpenses}
                            onChange={(e) => setFormData({ ...formData, monthlyExpenses: e.target.value })}
                            placeholder="3,500"
                            step="500"
                            min="0"
                            className="w-40 font-heading text-3xl text-center py-4 border-b-3 bg-transparent focus:outline-none transition"
                            style={{ color: '#2A1E5C', borderBottomWidth: '3px', borderColor: '#E4DCD2' }}
                            data-testid="expenses-input"
                            autoFocus
                          />
                          <span className="font-body text-sm" style={{ color: '#A89F8F' }}>/month</span>
                        </div>

                        {/* Switch path */}
                        <button
                          onClick={() => setFormData({ ...formData, hasTargetInMind: true, monthlyExpenses: '', entryPathSelected: false })}
                          className="mt-8 font-body text-sm underline decoration-dotted underline-offset-4 hover:opacity-80 transition"
                          style={{ color: '#8B7E6A' }}
                          data-testid="switch-path"
                        >
                          ← Choose a different starting point
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* Step: Debt - with timeline calculation */}
                {currentStepId === 'debt' && (
                  <div className="flex-1 flex flex-col items-center justify-center animate-fade-up-soft">
                    <div className="w-20 h-20 blob-3 flex items-center justify-center mb-6" style={{ background: 'rgba(222, 60, 75, 0.15)' }}>
                      <CreditCard className="w-10 h-10" style={{ color: '#DE3C4B' }} />
                    </div>
                    <h3 className="font-heading text-2xl font-medium mb-2 text-center" style={{ color: '#2A1E5C' }}>
                      What's your debt payoff goal for the next 12 months?
                    </h3>
                    <p className="font-body text-sm mb-8 text-center max-w-md" style={{ color: '#8B7E6A' }}>
                      Debt often extends beyond a year, but let's focus on what's achievable in the next 12 months.
                    </p>
                    <div className="w-full max-w-sm space-y-6">
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 font-heading text-2xl" style={{ color: '#C4B8A8' }}>$</span>
                        <input
                          type="number"
                          value={formData.debtAmount}
                          onChange={(e) => setFormData({ ...formData, debtAmount: e.target.value })}
                          placeholder="5,000"
                          step="500"
                          min="0"
                          className="w-full font-heading text-3xl text-center pl-10 pr-4 py-4 border-b-3 bg-transparent focus:outline-none transition"
                          style={{ color: '#2A1E5C', borderBottomWidth: '3px', borderColor: '#E4DCD2' }}
                          data-testid="debt-input"
                          autoFocus
                        />
                      </div>

                      {formData.debtAmount && parseFloat(formData.debtAmount) > 0 && (
                        <div>
                          <label className="font-body text-sm block mb-3 text-center" style={{ color: '#8B7E6A' }}>
                            How many months to pay it off?
                          </label>
                          <div className="flex justify-center gap-2 flex-wrap">
                            {[3, 6, 9, 12].map((months) => (
                              <button
                                key={months}
                                onClick={() => setFormData({ ...formData, debtMonths: months.toString() })}
                                className={`px-4 py-2 rounded-full font-body text-sm transition-all ${
                                  formData.debtMonths === months.toString()
                                    ? 'bg-red-500 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                                data-testid={`debt-months-${months}`}
                              >
                                {months} months
                              </button>
                            ))}
                          </div>
                          <div className="mt-4">
                            <input
                              type="range"
                              min="1"
                              max="12"
                              value={formData.debtMonths || 6}
                              onChange={(e) => setFormData({ ...formData, debtMonths: e.target.value })}
                              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-500"
                              data-testid="debt-months-slider"
                            />
                            <p className="font-body text-sm text-center mt-2" style={{ color: '#DE3C4B' }}>
                              {formData.debtMonths || 6} month{(formData.debtMonths || 6) !== '1' ? 's' : ''}
                            </p>
                          </div>

                          {/* Target-path heads up: debt is carved OUT of the monthly target */}
                          {formData.hasTargetInMind && formData.monthlyTarget && (() => {
                            const months = parseInt(formData.debtMonths) || 6;
                            const debtAmt = parseFloat(formData.debtAmount) || 0;
                            const monthlyPayment = debtAmt / months;
                            const paymentAt12 = debtAmt / 12;
                            const targetNum = parseFloat(formData.monthlyTarget) || 0;
                            return (
                              <div className="mt-4 text-center p-4 rounded-xl" style={{ background: 'rgba(222, 60, 75, 0.08)', border: '1px solid rgba(222, 60, 75, 0.25)' }} data-testid="debt-target-heads-up">
                                <p className="font-body text-sm mb-1" style={{ color: '#DE3C4B' }}>
                                  {formatCurrency(debtAmt)} ÷ {months} months = <strong>{formatCurrency(monthlyPayment)}/month</strong> toward this debt
                                </p>
                                <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>
                                  That comes <strong>out of</strong> your <strong style={{ color: '#2A1E5C' }}>{formatCurrency(targetNum)}/month</strong> target, we'll flag on the next screen if it doesn't leave enough for your expenses.
                                </p>
                                {months < 12 && (
                                  <button
                                    onClick={() => setFormData({ ...formData, debtMonths: '12' })}
                                    className="mt-3 px-4 py-2 rounded-full font-body text-sm transition-all hover:scale-[1.02]"
                                    style={{ background: 'rgba(76, 175, 80, 0.15)', color: '#2E7D32' }}
                                    data-testid="debt-stretch-suggestion"
                                  >
                                    Stretch it to 12 months and it's about <strong>{formatCurrency(paymentAt12)}/month</strong> instead →
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      <ToolLink tool="debt" label="Not sure? Work it out in the Debt Strategist" />

                      {/* Skip option */}
                      <button
                        onClick={() => {
                          setFormData({ ...formData, debtAmount: '', debtMonths: '' });
                          setCurrentStep(prev => prev + 1);
                        }}
                        className="w-full py-3 font-body text-sm transition-all rounded-lg hover:bg-gray-100"
                        style={{ color: '#8B7E6A', background: 'rgba(139, 126, 106, 0.1)' }}
                        data-testid="skip-debt"
                      >
                        I'm not focusing on debt this year
                      </button>
                    </div>
                  </div>
                )}

                {/* Step: Savings - habit-based, no timeline */}
                {currentStepId === 'savings' && (
                  <div className="flex-1 flex flex-col items-center justify-center animate-fade-up-soft">
                    <div className="w-20 h-20 blob-1 flex items-center justify-center mb-6" style={{ background: 'rgba(46, 134, 171, 0.15)' }}>
                      <PiggyBank className="w-10 h-10" style={{ color: '#2E86AB' }} />
                    </div>
                    <h3 className="font-heading text-2xl font-medium mb-2 text-center" style={{ color: '#2A1E5C' }}>
                      How much do you want to save this year?
                    </h3>
                    <p className="font-body text-sm mb-8 text-center max-w-md" style={{ color: '#8B7E6A' }}>
                      Unlike debt, savings isn't a race. We'll spread this across the year so it becomes part of your weekly rhythm.
                    </p>
                    <div className="w-full max-w-sm space-y-6">
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 font-heading text-2xl" style={{ color: '#C4B8A8' }}>$</span>
                        <input
                          type="number"
                          value={formData.savingsGoal}
                          onChange={(e) => {
                            setFormData({ ...formData, savingsGoal: e.target.value, savingsMonths: '12' });
                          }}
                          placeholder="3,000"
                          step="500"
                          min="0"
                          className="w-full font-heading text-3xl text-center pl-10 pr-4 py-4 border-b-3 bg-transparent focus:outline-none transition"
                          style={{ color: '#2A1E5C', borderBottomWidth: '3px', borderColor: '#E4DCD2' }}
                          data-testid="savings-input"
                          autoFocus
                        />
                      </div>
                      {formData.savingsGoal && parseFloat(formData.savingsGoal) > 0 && (
                        <div className="text-center p-4 rounded-xl" style={{ background: 'rgba(46, 134, 171, 0.1)' }}>
                          <p className="font-body text-base mb-1" style={{ color: '#2E86AB' }}>
                            That's about <strong>{formatCurrency(parseFloat(formData.savingsGoal) / 48)}/week</strong> toward your future self
                          </p>
                          <p className="font-body text-sm" style={{ color: '#8B7E6A' }}>
                            or about {formatCurrency(parseFloat(formData.savingsGoal) / 12)}/month
                          </p>
                          {formData.hasTargetInMind && formData.monthlyTarget && (() => {
                            const monthlySavings = parseFloat(formData.savingsGoal) / 12;
                            const targetNum = parseFloat(formData.monthlyTarget) || 0;
                            return (
                              <p className="font-body text-sm mt-3 pt-3 border-t" style={{ color: '#2A1E5C', borderColor: 'rgba(46, 134, 171, 0.2)' }} data-testid="savings-target-heads-up">
                                That's about <strong>{formatCurrency(monthlySavings)}/month</strong> carved out of your <strong>{formatCurrency(targetNum)}/month</strong> target. We'll flag on the next screen if it doesn't leave enough for your expenses.
                              </p>
                            );
                          })()}
                        </div>
                      )}

                      <ToolLink tool="savings" label="Not sure? Split it up in the Savings Goal Splitter" />

                      {/* Skip option */}
                      <button
                        onClick={() => {
                          setFormData({ ...formData, savingsGoal: '', savingsMonths: '' });
                          setCurrentStep(prev => prev + 1);
                        }}
                        className="w-full py-3 font-body text-sm transition-all rounded-lg hover:bg-gray-100"
                        style={{ color: '#8B7E6A', background: 'rgba(139, 126, 106, 0.1)' }}
                        data-testid="skip-savings"
                      >
                        I'm not focusing on savings this year
                      </button>
                    </div>
                  </div>
                )}

                {/* Step: Weeks Off */}
                {currentStepId === 'weeks' && (
                  <div className="flex-1 flex flex-col items-center justify-center animate-fade-up-soft">
                    <div className="w-20 h-20 blob-2 flex items-center justify-center mb-6" style={{ background: 'rgba(17, 75, 95, 0.15)' }}>
                      <Calendar className="w-10 h-10" style={{ color: '#114B5F' }} />
                    </div>
                    <h3 className="font-heading text-2xl font-medium mb-2 text-center" style={{ color: '#2A1E5C' }}>
                      How many weeks do you want to take off this year?
                    </h3>
                    <p className="font-body text-sm mb-8 text-center max-w-md" style={{ color: '#8B7E6A' }}>
                      This determines how many working weeks you'll have and builds in funded time off.
                    </p>
                    <div className="w-full max-w-md space-y-3">
                      {WEEKS_OFF_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setFormData({ ...formData, weeksOff: option.value })}
                          className={`w-full p-4 rounded-xl text-left transition-all flex items-center justify-between ${
                            formData.weeksOff === option.value
                              ? 'bg-white shadow-lg ring-2 ring-yellow-400'
                              : 'bg-gray-50 hover:bg-white hover:shadow-md'
                          }`}
                          data-testid={`weeks-off-${option.value}`}
                        >
                          <div>
                            <span className="font-heading font-medium" style={{ color: '#2A1E5C' }}>{option.label}</span>
                            <span className="font-body text-sm ml-2" style={{ color: '#8B7E6A' }}>({option.description})</span>
                          </div>
                          {formData.weeksOff === option.value && (
                            <CheckCircle2 className="w-5 h-5 text-yellow-500" />
                          )}
                        </button>
                      ))}

                      {formData.weeksOff === 'custom' && (
                        <div className="mt-4">
                          <input
                            type="number"
                            value={formData.customWeeksOff}
                            onChange={(e) => setFormData({ ...formData, customWeeksOff: e.target.value })}
                            placeholder="Enter weeks off (0-51)"
                            min="0"
                            max="51"
                            className="w-full font-body text-lg text-center p-3 rounded-xl bg-white border-2 border-yellow-400 focus:outline-none"
                            style={{ color: '#2A1E5C' }}
                            data-testid="custom-weeks-off-input"
                            autoFocus
                          />
                        </div>
                      )}

                      <ToolLink tool="pto" label="Not sure? Price it out in the PTO Buffer Planner" />
                    </div>
                  </div>
                )}

                {/* Step: Days Per Week */}
                {currentStepId === 'days' && (
                  <div className="flex-1 flex flex-col items-center justify-center animate-fade-up-soft">
                    <div className="w-20 h-20 blob-1 flex items-center justify-center mb-6" style={{ background: 'rgba(245, 203, 92, 0.15)' }}>
                      <Clock className="w-10 h-10" style={{ color: '#F5CB5C' }} />
                    </div>
                    <h3 className="font-heading text-2xl font-medium mb-2 text-center" style={{ color: '#2A1E5C' }}>
                      How many days a week do you want to work?
                    </h3>
                    <p className="font-body text-sm mb-8 text-center max-w-md" style={{ color: '#8B7E6A' }}>
                      Choose the number of days you realistically want to dedicate to this work each week.
                    </p>
                    <div className="flex justify-center gap-3 flex-wrap">
                      {[1, 2, 3, 4, 5, 6, 7].map((days) => (
                        <button
                          key={days}
                          onClick={() => setFormData({ ...formData, daysPerWeek: days })}
                          className={`w-14 h-14 rounded-2xl font-heading text-xl font-medium transition-all ${
                            formData.daysPerWeek === days
                              ? 'bg-yellow-400 text-gray-900 shadow-lg scale-110'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          data-testid={`days-${days}`}
                        >
                          {days}
                        </button>
                      ))}
                    </div>
                    <p className="font-body text-sm mt-4 text-center" style={{ color: '#8B7E6A' }}>
                      {formData.daysPerWeek} day{formData.daysPerWeek !== 1 ? 's' : ''} per week
                    </p>
                  </div>
                )}

                {/* Step: Taxes */}
                {currentStepId === 'taxes' && (
                  <div className="flex-1 flex flex-col items-center justify-center animate-fade-up-soft">
                    <div className="w-20 h-20 blob-2 flex items-center justify-center mb-6" style={{ background: 'rgba(222, 60, 75, 0.15)' }}>
                      <DollarSign className="w-10 h-10" style={{ color: '#DE3C4B' }} />
                    </div>
                    <h3 className="font-heading text-2xl font-medium mb-2 text-center" style={{ color: '#2A1E5C' }}>
                      Let's account for your taxes
                    </h3>
                    <p className="font-body text-sm mb-6 text-center max-w-lg" style={{ color: '#8B7E6A' }}>
                      As a 1099 worker, you're responsible for self-employment and federal income taxes.
                      We'll calculate what you need to <strong>gross</strong> to <strong>net</strong> your goal.
                    </p>

                    {/* Self-Employment Tax - Fixed */}
                    <div className="w-full max-w-md mb-6 p-4 rounded-xl" style={{ background: 'rgba(222, 60, 75, 0.08)' }}>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-heading font-medium" style={{ color: '#2A1E5C' }}>Self-Employment Tax</p>
                          <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>Social Security + Medicare (required for all 1099 income)</p>
                        </div>
                        <span className="font-heading text-xl font-medium" style={{ color: '#DE3C4B' }}>14.1%</span>
                      </div>
                      <p className="font-body text-xs mt-2 italic" style={{ color: '#A89F8F' }}>
                        Why 14.1%? Self-employment tax is 15.3%, but it's only applied to 92.35% of net income, making the effective rate about 14.1% for planning.
                      </p>
                    </div>

                    {/* Federal Income Tax - Slider */}
                    <div className="w-full max-w-md mb-4">
                      <div className="flex justify-between items-center mb-3">
                        <p className="font-heading font-medium" style={{ color: '#2A1E5C' }}>Federal Income Tax</p>
                        <span className="font-heading text-xl font-medium" style={{ color: '#2E86AB' }}>{formData.federalTaxRate}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="15"
                        value={formData.federalTaxRate}
                        onChange={(e) => setFormData({ ...formData, federalTaxRate: parseInt(e.target.value) })}
                        className="w-full h-3 rounded-full appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #2E86AB 0%, #2E86AB ${(formData.federalTaxRate / 15) * 100}%, #E4DCD2 ${(formData.federalTaxRate / 15) * 100}%, #E4DCD2 100%)`
                        }}
                        data-testid="federal-tax-slider"
                      />
                      <div className="flex justify-between mt-2">
                        <span className="font-body text-xs" style={{ color: '#8B7E6A' }}>0%</span>
                        <span className="font-body text-xs" style={{ color: '#8B7E6A' }}>15%</span>
                      </div>
                      <p className="font-body text-xs text-center mt-3" style={{ color: '#A89F8F' }}>
                        Most people set aside 10-15%. Choose 0% if you qualify for exemptions (like foreign earned income exclusion).
                      </p>
                    </div>

                    {/* Total Tax Rate */}
                    <div className="w-full max-w-md p-4 rounded-xl" style={{ background: 'rgba(42, 30, 92, 0.05)' }}>
                      <div className="flex justify-between items-center">
                        <p className="font-heading font-medium" style={{ color: '#2A1E5C' }}>Total Tax Set-Aside</p>
                        <span className="font-heading text-2xl font-medium" style={{ color: '#2A1E5C' }}>
                          {(14.1 + formData.federalTaxRate).toFixed(1)}%
                        </span>
                      </div>
                    </div>

                    {/* Optional: estimated payout cost (NOT a tax) */}
                    <div className="w-full max-w-md mt-6 pt-6 border-t" style={{ borderColor: '#E4DCD2' }} data-testid="payout-fee-section">
                      <p className="font-heading font-medium mb-1" style={{ color: '#2A1E5C' }}>
                        Do you usually pay a fee to receive or transfer your payouts?
                      </p>
                      <p className="font-body text-xs mb-3" style={{ color: '#8B7E6A' }}>
                        Optional, and separate from taxes. Some workers lose a percentage to instant-transfer or
                        payout fees (for example PayPal or Stripe).
                      </p>
                      <div className="flex gap-2 mb-1">
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, payoutFeeChoice: 'no' })}
                          className="flex-1 py-2.5 rounded-xl font-heading text-sm font-medium transition-all"
                          style={formData.payoutFeeChoice === 'no'
                            ? { background: '#2A1E5C', color: '#fff' }
                            : { background: 'rgba(42,30,92,0.06)', color: '#8B7E6A' }}
                          data-testid="payout-fee-no"
                        >
                          No, 0%
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, payoutFeeChoice: 'yes' })}
                          className="flex-1 py-2.5 rounded-xl font-heading text-sm font-medium transition-all"
                          style={formData.payoutFeeChoice === 'yes'
                            ? { background: '#2A1E5C', color: '#fff' }
                            : { background: 'rgba(42,30,92,0.06)', color: '#8B7E6A' }}
                          data-testid="payout-fee-yes"
                        >
                          Yes, I pay a fee
                        </button>
                      </div>
                      {formData.payoutFeeChoice === '' && (
                        <p className="font-body text-xs mt-2" style={{ color: '#8B7E6A' }}>
                          Not sure? Leave this untouched &mdash; a brand-new sheet starts at 0%, and an existing sheet keeps whatever rate it already has.
                        </p>
                      )}
                      {formData.payoutFeeChoice === 'yes' && (
                        <div className="mt-4 p-4 rounded-xl" style={{ background: 'rgba(230, 168, 60, 0.1)' }}>
                          <label className="font-heading font-medium block mb-2" style={{ color: '#2A1E5C' }}>
                            Estimated payout fee
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              max="60"
                              step="0.01"
                              value={formData.payoutFeeRate}
                              onChange={(e) => setFormData({ ...formData, payoutFeeRate: e.target.value })}
                              className="flex-1 px-4 py-2.5 rounded-xl font-heading text-lg focus:outline-none"
                              style={{ background: '#fff', border: '1px solid #E4DCD2', color: '#2A1E5C' }}
                              data-testid="payout-fee-input"
                            />
                            <span className="font-heading text-lg font-medium" style={{ color: '#B8860B' }}>%</span>
                          </div>
                          <p className="font-body text-xs mt-3" style={{ color: '#A89F8F' }}>
                            Enter the exact percentage you pay (for example 1.5). We'll apply this estimate to each
                            payout's gross and set it aside separately from your taxes. Your actual fee may differ,
                            since some providers charge on the amount transferred.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Step: Buffer (Expenses path only) */}
                {currentStepId === 'buffer' && (
                  <div className="flex flex-col items-center">
                    <h3 className="font-heading text-2xl sm:text-3xl font-medium mb-3 text-center" style={{ color: '#2A1E5C' }}>
                      Add a buffer for slow weeks?
                    </h3>
                    <p className="font-body text-base mb-8 max-w-lg text-center" style={{ color: '#5D5A6B' }}>
                      Based on your expenses and goals, we've calculated the <strong>minimum</strong> you need to earn.
                      Adding a buffer gives you breathing room when work is slow.
                    </p>

                    {/* Buffer Slider */}
                    <div className="w-full max-w-md mb-6">
                      <div className="flex justify-between items-center mb-3">
                        <p className="font-heading font-medium" style={{ color: '#2A1E5C' }}>Buffer for Slow Weeks</p>
                        <span className="font-heading text-xl font-medium" style={{ color: '#2A1E5C' }}>{formData.slowWeekBuffer}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="30"
                        step="5"
                        value={formData.slowWeekBuffer}
                        onChange={(e) => setFormData({ ...formData, slowWeekBuffer: parseInt(e.target.value) })}
                        className="w-full h-3 rounded-full appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #2A1E5C 0%, #2A1E5C ${(formData.slowWeekBuffer / 30) * 100}%, #E4DCD2 ${(formData.slowWeekBuffer / 30) * 100}%, #E4DCD2 100%)`
                        }}
                        data-testid="buffer-slider"
                      />
                      <div className="flex justify-between mt-2">
                        <span className="font-body text-xs" style={{ color: '#8B7E6A' }}>0% (minimum)</span>
                        <span className="font-body text-xs" style={{ color: '#8B7E6A' }}>30% (comfortable)</span>
                      </div>
                    </div>

                    {/* Buffer explanation */}
                    <div className="w-full max-w-md p-4 rounded-xl" style={{ background: 'rgba(42, 30, 92, 0.05)' }}>
                      <p className="font-body text-sm text-center" style={{ color: '#5D5A6B' }}>
                        {formData.slowWeekBuffer === 0 ? (
                          <>This is your <strong>exact minimum</strong>. Every week needs to hit your target. No room for slow weeks.</>
                        ) : formData.slowWeekBuffer <= 10 ? (
                          <>A <strong>{formData.slowWeekBuffer}% buffer</strong> gives you a small cushion. One or two slow weeks won't derail you.</>
                        ) : formData.slowWeekBuffer <= 20 ? (
                          <>A <strong>{formData.slowWeekBuffer}% buffer</strong> is solid. You'll have breathing room for a slow month without stress.</>
                        ) : (
                          <>A <strong>{formData.slowWeekBuffer}% buffer</strong> is comfortable. You'll bank a bonus even in slower seasons.</>
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex justify-between items-center mt-10 pt-8 border-t" style={{ borderColor: 'rgba(42, 30, 92, 0.08)' }}>
                  <button
                    onClick={handleBack}
                    disabled={currentStep === 0}
                    className={`btn-soft btn-secondary-soft flex items-center gap-2 ${currentStep === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
                    data-testid="back-btn"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </button>
                  <button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className={`btn-soft btn-primary-soft flex items-center gap-2 ${!canProceed() ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid="next-btn"
                  >
                    {currentStep === steps.length - 1 ? 'See My Number' : 'Next'}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Results */
            <div id="results-section" className="animate-fade-up-soft" data-testid="results">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: 'rgba(42, 30, 92, 0.1)' }}>
                  <Sparkles className="w-4 h-4" style={{ color: '#2A1E5C' }} />
                  <span className="font-body text-sm" style={{ color: '#2A1E5C' }}>Your weekly income target (GROSS)</span>
                </div>
                <h3 className="font-heading text-5xl sm:text-6xl font-medium mb-2" style={{ color: '#2A1E5C' }}>
                  {formatCurrency(calculateRelayTarget())}
                  <span className="text-2xl font-normal" style={{ color: '#5D5A6B' }}>/week</span>
                </h3>
                <p className="font-heading text-2xl mb-4" style={{ color: '#F5CB5C' }}>
                  {formatCurrency(calculateDailyTarget())}
                  <span className="text-lg font-normal" style={{ color: '#8B7E6A' }}>/day average</span>
                </p>
                <p className="font-body text-base max-w-xl mx-auto mb-6" style={{ color: '#5D5A6B' }}>
                  {formData.hasTargetInMind && formData.monthlyTarget ? (
                    <>
                      This is what you need to <strong style={{ color: '#2A1E5C' }}>gross</strong> each week in order to
                      <strong style={{ color: '#2A1E5C' }}> take home {formatCurrency(parseFloat(formData.monthlyTarget))}/month</strong> after taxes.
                    </>
                  ) : (
                    <>
                      This is what you need to <strong style={{ color: '#2A1E5C' }}>gross</strong> each week. After taxes,
                      you'll <strong style={{ color: '#2A1E5C' }}>net</strong> {formatCurrency(getTaxBreakdown().netWeekly)}/week to cover your goals.
                    </>
                  )}
                </p>

                {/* SMART Reality Check Section */}
                {(() => {
                  const feasibility = getFeasibilityCheck();
                  const statusConfig = {
                    'on-track': {
                      icon: '✓',
                      label: 'On Track!',
                      bgColor: 'rgba(76, 175, 80, 0.15)',
                      borderColor: '#4CAF50',
                      textColor: '#4CAF50'
                    },
                    'solid': {
                      icon: '✓',
                      label: 'Solid!',
                      bgColor: 'rgba(76, 175, 80, 0.15)',
                      borderColor: '#4CAF50',
                      textColor: '#4CAF50'
                    },
                    'nice': {
                      icon: '✓',
                      label: 'Nice!',
                      bgColor: 'rgba(76, 175, 80, 0.15)',
                      borderColor: '#4CAF50',
                      textColor: '#4CAF50'
                    },
                    'great': {
                      icon: '✓',
                      label: 'Great!',
                      bgColor: 'rgba(76, 175, 80, 0.15)',
                      borderColor: '#4CAF50',
                      textColor: '#4CAF50'
                    },
                    'perfect': {
                      icon: '✓',
                      label: 'Perfect!',
                      bgColor: 'rgba(76, 175, 80, 0.15)',
                      borderColor: '#4CAF50',
                      textColor: '#4CAF50'
                    },
                    'tight': {
                      icon: '→',
                      label: 'Tight!',
                      bgColor: 'rgba(255, 193, 7, 0.15)',
                      borderColor: '#FFC107',
                      textColor: '#FFC107'
                    },
                    'not-realistic': {
                      icon: '!',
                      label: 'Uh-oh!',
                      bgColor: 'rgba(222, 60, 75, 0.15)',
                      borderColor: '#DE3C4B',
                      textColor: '#DE3C4B'
                    },
                    'unknown': {
                      icon: '✓',
                      label: 'Sanity Check',
                      bgColor: 'rgba(46, 134, 171, 0.15)',
                      borderColor: '#2E86AB',
                      textColor: '#2E86AB'
                    }
                  };
                  const config = statusConfig[feasibility.status] || statusConfig['unknown'];

                  return (
                    <div className="max-w-lg mx-auto mb-8">
                      <div
                        className="p-5 rounded-2xl border-2"
                        style={{
                          background: config.bgColor,
                          borderColor: config.borderColor
                        }}
                        data-testid="feasibility-check"
                      >
                        <div className="flex items-center justify-center gap-3 mb-3">
                          <span
                            className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-lg"
                            style={{ background: config.borderColor, color: '#2A1E5C' }}
                          >
                            {config.icon}
                          </span>
                          <span
                            className="font-heading text-xl font-medium"
                            style={{ color: config.textColor }}
                          >
                            {config.label}
                          </span>
                        </div>

                        <p className="font-body text-sm text-center mb-4" style={{ color: '#2A1E5C' }}>
                          {feasibility.message}
                        </p>

                        {feasibility.suggestion && (
                          <p className="font-body text-xs text-center italic" style={{ color: '#5D5A6B' }}>
                            {feasibility.suggestion}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Tax Breakdown */}
                <div className="max-w-lg mx-auto mb-6" data-testid="tax-breakdown">
                  <div className="p-5 rounded-2xl" style={{ background: 'rgba(42, 30, 92, 0.05)' }}>
                    <p className="font-body text-xs uppercase tracking-widest mb-4 text-center" style={{ color: '#5D5A6B' }}>Tax Breakdown</p>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="font-body text-sm" style={{ color: '#5D5A6B' }}>Self-employment tax (14.1%)</span>
                        <span className="font-heading font-medium" style={{ color: '#DE3C4B' }}>−{formatCurrency(getTaxBreakdown().selfEmploymentTax)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-body text-sm" style={{ color: '#5D5A6B' }}>Federal income tax ({formData.federalTaxRate}%)</span>
                        <span className="font-heading font-medium" style={{ color: '#DE3C4B' }}>−{formatCurrency(getTaxBreakdown().federalTax)}</span>
                      </div>
                      <div className="border-t pt-4 mt-4" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
                        <div className="text-center">
                          <p className="font-body text-sm mb-2" style={{ color: '#5D5A6B' }}>Your weekly take-home</p>
                          <p className="font-heading text-4xl font-medium mb-1" style={{ color: '#4CAF50' }}>{formatCurrency(getTaxBreakdown().netWeekly)}</p>
                          <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>What you keep after taxes to cover your goals</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ========== FINANCIAL TRAJECTORY SECTION (always shows) ========== */}
              {(() => {
                const story = getTrajectoryStory();
                const weeklyTarget = calculateRelayTarget();
                const dailyTarget = calculateDailyTarget();
                const daysPerWeek = parseInt(formData.daysPerWeek) || 5;
                const workWeeks = getWorkingWeeks();

                // STORY 1: Expenses Only - "Your Steady Rhythm"
                if (story === 'expenses-only') {
                  const weeksOff = 52 - workWeeks;
                  const timeOffBufferPerWeek = getTimeOffBuffer();
                  const totalTimeOffFund = timeOffBufferPerWeek * workWeeks;
                  return (
                    <div className="max-w-4xl mx-auto mt-16" data-testid="timeline-section">
                      <div className="p-8 sm:p-10 rounded-2xl" style={{ background: '#1E1640' }}>
                        <div className="text-center mb-8">
                          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: 'rgba(245, 203, 92, 0.15)' }}>
                            <Calendar className="w-4 h-4" style={{ color: '#F5CB5C' }} />
                            <span className="font-body text-sm" style={{ color: '#F5CB5C' }}>Your 12-Month Rhythm</span>
                          </div>
                          <h3 className="font-heading text-2xl sm:text-3xl font-medium mb-2" style={{ color: '#FFFFFF' }}>
                            Stability through consistency.
                          </h3>
                          <p className="font-body text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                            Hit your weekly target, week after week, and your expenses are covered all year.
                          </p>
                        </div>
                        <div className="relative h-3 rounded-full overflow-hidden mb-8" style={{ background: 'rgba(255,255,255,0.1)' }}>
                          <div className="absolute h-full w-full" style={{ background: '#F5CB5C' }} />
                        </div>
                        <div className="p-8 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.05)', border: '2px solid #F5CB5C' }}>
                          <p className="font-body text-xs uppercase tracking-wider mb-4" style={{ color: '#F5CB5C' }}>Your Weekly Target</p>
                          <p className="font-heading text-5xl font-medium mb-2" style={{ color: '#FFFFFF' }}>{formatCurrency(weeklyTarget)}</p>
                          <p className="font-heading text-xl mb-4" style={{ color: '#F5CB5C' }}>{formatCurrency(dailyTarget)}/day × {daysPerWeek} days</p>

                          {weeksOff > 0 && (
                            <>
                              <p className="font-body text-sm mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>
                                Includes each week: <span style={{ color: '#FFFFFF' }}>{formatCurrency(timeOffBufferPerWeek)} towards time off</span>
                              </p>

                              <div className="mb-6 p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
                                <p className="font-body text-sm mb-3 font-medium" style={{ color: '#FFFFFF' }}>Your {weeksOff}-week time-off fund</p>
                                <div className="flex justify-center gap-8 text-center">
                                  <div>
                                    <p className="font-body text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Total saved</p>
                                    <p className="font-heading text-xl font-medium" style={{ color: '#FFFFFF' }}>{formatCurrency(totalTimeOffFund)}</p>
                                  </div>
                                  <div>
                                    <p className="font-body text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>For each week you take off</p>
                                    <p className="font-heading text-xl font-medium" style={{ color: '#FFFFFF' }}>{formatCurrency(totalTimeOffFund / weeksOff)}</p>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}

                          <div className="pt-6 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                            <p className="font-body text-base" style={{ color: 'rgba(255,255,255,0.9)' }}>{workWeeks} weeks of hitting this target = <span style={{ color: '#F5CB5C' }}>a year of stability</span></p>
                          </div>
                        </div>
                        <p className="font-body text-sm text-center mt-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Beat your target? That extra goes to your bonus bank, ready for debt payoff, savings, or breathing room.</p>
                      </div>
                    </div>
                  );
                }

                // TARGET MODE - User has a specific monthly take-home target
                if (story === 'target-mode') {
                  const monthlyTarget = parseFloat(formData.monthlyTarget) || 0;
                  const weeksOff = 52 - workWeeks;
                  const federalTaxRate = (formData.federalTaxRate || 0) / 100;
                  const totalTaxRate = SELF_EMPLOYMENT_TAX + federalTaxRate;
                  // monthlyTarget IS the net (take-home). Gross is derived by adding taxes on top.
                  const monthlyNet = monthlyTarget;
                  const monthlyGross = totalTaxRate < 1 ? monthlyTarget / (1 - totalTaxRate) : monthlyTarget;
                  const timeOffBufferPerWeek = getTimeOffBuffer();
                  const totalTimeOffFund = timeOffBufferPerWeek * workWeeks;
                  const annualGross = monthlyGross * 12;
                  const annualNet = monthlyNet * 12;

                  return (
                    <div className="max-w-4xl mx-auto mt-16" data-testid="timeline-section">
                      <div className="p-8 sm:p-10 rounded-2xl" style={{ background: '#1E1640' }}>
                        <div className="text-center mb-8">
                          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: 'rgba(245, 203, 92, 0.15)' }}>
                            <Target className="w-4 h-4" style={{ color: '#F5CB5C' }} />
                            <span className="font-body text-sm" style={{ color: '#F5CB5C' }}>Your Income Target</span>
                          </div>
                          <h3 className="font-heading text-2xl sm:text-3xl font-medium mb-2" style={{ color: '#FFFFFF' }}>
                            Your path to taking home {formatCurrency(monthlyTarget)}/month.
                          </h3>
                          <p className="font-body text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                            Whether it's for rent, residency, or a qualification, here's what it takes.
                          </p>
                        </div>
                        <div className="relative h-3 rounded-full overflow-hidden mb-8" style={{ background: 'rgba(255,255,255,0.1)' }}>
                          <div className="absolute h-full w-full" style={{ background: '#F5CB5C' }} />
                        </div>
                        <div className="p-8 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.05)', border: '2px solid #F5CB5C' }}>
                          <p className="font-body text-xs uppercase tracking-wider mb-4" style={{ color: '#F5CB5C' }}>Your Weekly Target</p>
                          <p className="font-heading text-5xl font-medium mb-2" style={{ color: '#FFFFFF' }}>{formatCurrency(weeklyTarget)}</p>
                          <p className="font-heading text-xl mb-4" style={{ color: '#F5CB5C' }}>{formatCurrency(dailyTarget)}/day × {daysPerWeek} days</p>

                          {weeksOff > 0 && (
                            <p className="font-body text-sm mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>
                              Includes each week: <span style={{ color: '#FFFFFF' }}>{formatCurrency(timeOffBufferPerWeek)} towards time off</span>
                            </p>
                          )}

                          <div className="mb-6 p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                            <div className="grid grid-cols-2 gap-4 text-center">
                              <div>
                                <p className="font-body text-xs uppercase mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Monthly Gross</p>
                                <p className="font-heading text-xl" style={{ color: '#FFFFFF' }}>{formatCurrency(monthlyGross)}</p>
                              </div>
                              <div>
                                <p className="font-body text-xs uppercase mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Annual Gross</p>
                                <p className="font-heading text-xl" style={{ color: '#FFFFFF' }}>{formatCurrency(annualGross)}</p>
                              </div>
                            </div>
                            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                              <p className="font-body text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>After taxes (~{Math.round(totalTaxRate * 100)}%), you'll take home:</p>
                              <p className="font-heading text-lg" style={{ color: '#4CAF50' }}>{formatCurrency(annualNet)}/year ({formatCurrency(monthlyNet)}/month)</p>
                            </div>
                          </div>

                          {weeksOff > 0 && (
                            <div className="mb-6 p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
                              <p className="font-body text-sm mb-3 font-medium" style={{ color: '#FFFFFF' }}>Your {weeksOff}-week time-off fund</p>
                              <div className="flex justify-center gap-8 text-center">
                                <div>
                                  <p className="font-body text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Total saved</p>
                                  <p className="font-heading text-xl font-medium" style={{ color: '#FFFFFF' }}>{formatCurrency(totalTimeOffFund)}</p>
                                </div>
                                <div>
                                  <p className="font-body text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>For each week you take off</p>
                                  <p className="font-heading text-xl font-medium" style={{ color: '#FFFFFF' }}>{formatCurrency(totalTimeOffFund / weeksOff)}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="pt-6 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                            <p className="font-body text-base" style={{ color: 'rgba(255,255,255,0.9)' }}>{workWeeks} weeks of hitting this target = <span style={{ color: '#F5CB5C' }}>your take-home goal of {formatCurrency(monthlyTarget)}/month achieved</span></p>
                          </div>
                        </div>
                        <p className="font-body text-sm text-center mt-4" style={{ color: 'rgba(255,255,255,0.5)' }}>This calculation accounts for self-employment tax and federal income tax so you know the real number to hit.</p>
                      </div>
                    </div>
                  );
                }

                // STORY 2: Debt Only - Two chapters
                if (story === 'debt-only') {
                  const debtMonths = parseInt(formData.debtMonths) || 6;
                  const debtAmount = parseFloat(formData.debtAmount) || 0;
                  // A 12-month payoff leaves no "after the debt" chapter, so show the steady-pace view.
                  if (debtMonths >= 12) {
                    return (
                      <SinglePaceTrajectory
                        weeklyTarget={weeklyTarget}
                        dailyTarget={dailyTarget}
                        daysPerWeek={daysPerWeek}
                        workWeeks={workWeeks}
                        setAsides={getGoalTimelines()}
                        timeOffBufferPerWeek={getTimeOffBuffer()}
                        formatCurrency={formatCurrency}
                      />
                    );
                  }
                  const federalTaxDecimal = (formData.federalTaxRate || 10) / 100;
                  const totalTaxRate = 0.153 + federalTaxDecimal;
                  const baseWeekly = (parseFloat(formData.monthlyExpenses) * 12 / workWeeks) / (1 - totalTaxRate);
                  const baseDaily = baseWeekly / daysPerWeek;
                  const remainingMonths = 12 - debtMonths;
                  const remainingWeeks = Math.round((remainingMonths / 12) * workWeeks);
                  const extraPerWeekIfKeep = weeklyTarget - baseWeekly;
                  const potentialSavingsMax = extraPerWeekIfKeep * remainingWeeks;
                  const weeksOff = 52 - workWeeks;
                  const timeOffBufferPerWeek = getTimeOffBuffer();
                  const totalTimeOffFund = timeOffBufferPerWeek * workWeeks;
                  const netWeeklyNeed = calculateNetWeeklyTarget();
                  const ptoFundIfKeep = extraPerWeekIfKeep * (1 - totalTaxRate) * remainingWeeks;
                  const extraWeeksOffFunded = netWeeklyNeed > 0 ? ptoFundIfKeep / netWeeklyNeed : 0;
                  return (
                    <div className="max-w-5xl mx-auto mt-16" data-testid="timeline-section">
                      <div className="text-center mb-8">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: 'rgba(222, 60, 75, 0.15)' }}>
                          <Calendar className="w-4 h-4" style={{ color: '#DE3C4B' }} />
                          <span className="font-body text-sm" style={{ color: '#DE3C4B' }}>Your 12-Month Trajectory</span>
                        </div>
                        <h3 className="font-heading text-2xl sm:text-3xl font-medium mb-2" style={{ color: '#2A1E5C' }}>Two chapters to your year.</h3>
                        <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>Pay off the debt, then unlock new possibilities.</p>
                      </div>

                      {weeksOff > 0 && (
                        <div className="mb-6 p-4 rounded-lg max-w-3xl mx-auto" style={{ background: 'rgba(42, 30, 92, 0.05)' }}>
                          <p className="font-body text-sm text-center mb-3 font-medium" style={{ color: '#2A1E5C' }}>Your {weeksOff}-week time-off fund</p>
                          <div className="flex justify-center gap-8 text-center">
                            <div>
                              <p className="font-heading text-lg font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(timeOffBufferPerWeek)}/week</p>
                              <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>Towards time off</p>
                            </div>
                            <div>
                              <p className="font-heading text-lg font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(totalTimeOffFund)}</p>
                              <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>Total saved</p>
                            </div>
                            <div>
                              <p className="font-heading text-lg font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(totalTimeOffFund / weeksOff)}</p>
                              <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>For each week you take off</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="relative h-3 rounded-full overflow-hidden mb-8" style={{ background: 'rgba(42, 30, 92, 0.1)' }}>
                        <div className="absolute h-full" style={{ left: '0%', width: `${(debtMonths / 12) * 100}%`, background: '#DE3C4B', borderRight: '2px solid rgba(255,255,255,0.3)' }} />
                        <div className="absolute h-full" style={{ left: `${(debtMonths / 12) * 100}%`, width: `${100 - (debtMonths / 12) * 100}%`, background: '#4CAF50' }} />
                      </div>
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Chapter 1: Payoff */}
                        <div className="p-6 rounded-xl flex flex-col" style={{ background: 'rgba(42, 30, 92, 0.05)', border: '2px solid #DE3C4B' }}>
                          <div className="flex items-center justify-between mb-6">
                            <span className="font-heading text-sm uppercase tracking-wider font-medium" style={{ color: '#DE3C4B' }}>Chapter 1: Payoff</span>
                            <span className="font-body text-xs" style={{ color: '#8B7E6A' }}>Months 1-{debtMonths}</span>
                          </div>
                          <div className="text-center flex-1 flex flex-col justify-center mb-6">
                            <p className="font-heading text-4xl lg:text-5xl font-medium mb-2" style={{ color: '#2A1E5C' }}>{formatCurrency(weeklyTarget)}</p>
                            <p className="font-heading text-lg" style={{ color: '#5D5A6B' }}>per week</p>
                            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
                              <p className="font-heading text-2xl font-medium" style={{ color: '#DE3C4B' }}>{formatCurrency(dailyTarget)}/day</p>
                              <p className="font-body text-sm mt-1" style={{ color: '#5D5A6B' }}>× {daysPerWeek} days a week</p>
                            </div>
                          </div>
                          <div className="pt-4 border-t mt-auto text-center" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
                            <CheckCircle2 className="w-6 h-6 mx-auto mb-2" style={{ color: '#DE3C4B' }} />
                            <span className="font-body text-sm" style={{ color: '#5D5A6B' }}>{formatCurrency(debtAmount)} debt cleared by</span>
                            <p className="font-heading text-2xl font-medium" style={{ color: '#DE3C4B' }}>Month {debtMonths}</p>
                          </div>
                        </div>

                        {/* Chapter 2: Freedom */}
                        <div className="p-6 rounded-xl flex flex-col" style={{ background: 'rgba(76, 175, 80, 0.08)', border: '2px solid #4CAF50' }}>
                          <div className="flex items-center justify-between mb-4">
                            <span className="font-heading text-sm uppercase tracking-wider font-medium" style={{ color: '#4CAF50' }}>Chapter 2: Freedom</span>
                            <span className="font-body text-xs" style={{ color: '#8B7E6A' }}>Months {debtMonths + 1}-12</span>
                          </div>

                          <div className="text-center mb-4">
                            <p className="font-body text-xs uppercase tracking-wider mb-2" style={{ color: '#8B7E6A' }}>Now all you need to cover expenses</p>
                            <p className="font-heading text-3xl font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(baseWeekly)}</p>
                            <p className="font-heading text-base" style={{ color: '#4CAF50' }}>{formatCurrency(baseDaily)}/day × {daysPerWeek} days</p>
                          </div>

                          <div className="pt-4 border-t" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
                            <p className="font-body text-xs text-center mb-3" style={{ color: '#5D5A6B' }}>
                              You said you weren't focusing on savings this year, but after your debt's paid off, here's what's possible:
                            </p>

                            <div className="p-4 rounded-lg mb-3" style={{ background: 'rgba(245, 203, 92, 0.15)' }}>
                              <p className="font-heading text-sm font-medium mb-2" style={{ color: '#F5CB5C' }}>Keep Your Momentum</p>
                              <p className="font-body text-xs mb-3" style={{ color: '#5D5A6B' }}>
                                Keep your original target of {formatCurrency(weeklyTarget)}/wk and put the extra towards savings.
                              </p>
                              <div className="text-center p-3 rounded-lg" style={{ background: 'rgba(42, 30, 92, 0.05)' }}>
                                <p className="font-body text-xs mb-1" style={{ color: '#8B7E6A' }}>Potential savings by year-end</p>
                                <p className="font-heading text-2xl font-medium" style={{ color: '#F5CB5C' }}>{formatCurrency(potentialSavingsMax)}</p>
                                <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>in {remainingMonths} months</p>
                              </div>
                            </div>

                            <div className="p-4 rounded-lg mb-3" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                              <p className="font-heading text-sm font-medium mb-2" style={{ color: '#2A1E5C' }}>More Breathing Room</p>
                              <p className="font-body text-xs mb-2" style={{ color: '#5D5A6B' }}>
                                Lower your target to between {formatCurrency(baseWeekly)}/wk and {formatCurrency(weeklyTarget)}/wk.
                              </p>
                              <p className="font-body text-xs" style={{ color: '#5D5A6B' }}>
                                Potential savings: {formatCurrency(0)} – {formatCurrency(potentialSavingsMax)}
                              </p>
                            </div>

                            <div className="p-4 rounded-lg" style={{ background: 'rgba(76, 175, 80, 0.12)' }} data-testid="buy-back-time-option">
                              <p className="font-heading text-sm font-medium mb-2" style={{ color: '#4CAF50' }}>Buy Back Time</p>
                              <p className="font-body text-xs mb-2" style={{ color: '#5D5A6B' }}>
                                Keep your original target and put the extra toward time off instead.
                              </p>
                              <p className="font-body text-xs" style={{ color: '#5D5A6B' }}>
                                That's about <strong style={{ color: '#2A1E5C' }}>{extraWeeksOffFunded.toFixed(1)} extra weeks off</strong>, fully funded, {formatCurrency(ptoFundIfKeep)} in your time-off fund by year-end.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }

                // STORY 3: Savings Only - Accumulation over year
                if (story === 'savings-only') {
                  const savingsGoal = parseFloat(formData.savingsGoal) || 0;
                  const weeksOff = 52 - workWeeks;
                  const timeOffBufferPerWeek = getTimeOffBuffer();
                  const totalTimeOffFund = timeOffBufferPerWeek * workWeeks;
                  const weeklySavingsAmount = savingsGoal / workWeeks;
                  const milestoneLabels = ['Q1 checkpoint', 'Halfway', 'Momentum', 'Goal reached'];
                  return (
                    <div className="max-w-4xl mx-auto mt-16" data-testid="timeline-section">
                      <div className="text-center mb-8">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: 'rgba(46, 134, 171, 0.15)' }}>
                          <Calendar className="w-4 h-4" style={{ color: '#2E86AB' }} />
                          <span className="font-body text-sm" style={{ color: '#2E86AB' }}>Your 12-Month Trajectory</span>
                        </div>
                        <h3 className="font-heading text-2xl sm:text-3xl font-medium mb-2" style={{ color: '#2A1E5C' }}>Building your safety net, week by week.</h3>
                        <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>Small consistent deposits add up to big security.</p>
                      </div>

                      <div className="relative h-3 rounded-full overflow-hidden mb-8" style={{ background: 'rgba(42, 30, 92, 0.1)' }}>
                        <div className="absolute h-full w-full" style={{ background: 'linear-gradient(to right, #2E86AB 0%, #4CAF50 100%)' }} />
                      </div>

                      <div className="p-8 rounded-xl" style={{ background: 'rgba(46, 134, 171, 0.08)', border: '2px solid #2E86AB' }}>
                        <div className="text-center mb-6">
                          <p className="font-body text-xs uppercase tracking-wider mb-4" style={{ color: '#2E86AB' }}>Your Weekly Target</p>
                          <p className="font-heading text-5xl font-medium mb-2" style={{ color: '#2A1E5C' }}>{formatCurrency(weeklyTarget)}</p>
                          <p className="font-heading text-xl mb-4" style={{ color: '#2E86AB' }}>{formatCurrency(dailyTarget)}/day × {daysPerWeek} days</p>

                          <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>
                            Includes each week: <span style={{ color: '#4CAF50' }}>{formatCurrency(weeklySavingsAmount)} towards savings</span>
                            {weeksOff > 0 && <span> + <span style={{ color: '#2A1E5C' }}>{formatCurrency(timeOffBufferPerWeek)} towards time off</span></span>}
                          </p>
                        </div>

                        <div className="grid grid-cols-4 gap-4 pt-6 border-t" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
                          {[3, 6, 9, 12].map((month, idx) => (
                            <div key={month} className="text-center">
                              <p className="font-body text-xs mb-1" style={{ color: month === 12 ? '#4CAF50' : '#8B7E6A' }}>{milestoneLabels[idx]}</p>
                              <p className="font-heading text-lg font-medium" style={{ color: month === 12 ? '#4CAF50' : '#2A1E5C' }}>{formatCurrency((savingsGoal / 12) * month)}</p>
                              <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>Month {month}</p>
                            </div>
                          ))}
                        </div>

                        <div className="mt-6 pt-4 border-t" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <CheckCircle2 className="w-5 h-5" style={{ color: '#4CAF50' }} />
                            <p className="font-body text-base" style={{ color: '#2A1E5C' }}>By month 12, you'll have <span className="font-medium" style={{ color: '#4CAF50' }}>{formatCurrency(savingsGoal)} in your safety net</span></p>
                          </div>
                          {weeksOff > 0 && (
                            <div className="mt-4 p-4 rounded-lg" style={{ background: 'rgba(42, 30, 92, 0.05)' }}>
                              <p className="font-body text-sm text-center mb-3 font-medium" style={{ color: '#2A1E5C' }}>Your {weeksOff}-week time-off fund</p>
                              <div className="flex justify-center gap-8 text-center">
                                <div>
                                  <p className="font-body text-xs mb-1" style={{ color: '#8B7E6A' }}>Total saved</p>
                                  <p className="font-heading text-xl font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(totalTimeOffFund)}</p>
                                </div>
                                <div>
                                  <p className="font-body text-xs mb-1" style={{ color: '#8B7E6A' }}>For each week you take off</p>
                                  <p className="font-heading text-xl font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(totalTimeOffFund / weeksOff)}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                // STORY 4: Debt + Savings - Full three-phase story
                if (story === 'debt-and-savings' && calculateMomentumOptions()) {
                  const options = calculateMomentumOptions();
                  const weeksOff = 52 - workWeeks;
                  const timeOffBufferPerWeek = getTimeOffBuffer();
                  const totalTimeOffFund = timeOffBufferPerWeek * workWeeks;
                  return (
                    <div className="max-w-6xl mx-auto mt-16" data-testid="timeline-section">
                      <div className="text-center mb-8">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: 'rgba(46, 134, 171, 0.15)' }}>
                          <Calendar className="w-4 h-4" style={{ color: '#2E86AB' }} />
                          <span className="font-body text-sm" style={{ color: '#2E86AB' }}>Your 12-Month Trajectory</span>
                        </div>
                        <h3 className="font-heading text-2xl sm:text-3xl font-medium mb-2" style={{ color: '#2A1E5C' }}>Here's how your year unfolds.</h3>
                        <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>After you clear your debt, you unlock new choices.</p>
                      </div>

                      {weeksOff > 0 && (
                        <div className="mb-6 p-4 rounded-lg max-w-3xl mx-auto" style={{ background: 'rgba(42, 30, 92, 0.05)' }}>
                          <p className="font-body text-sm text-center mb-3 font-medium" style={{ color: '#2A1E5C' }}>Your {weeksOff}-week time-off fund</p>
                          <div className="flex justify-center gap-8 text-center">
                            <div>
                              <p className="font-heading text-lg font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(timeOffBufferPerWeek)}/week</p>
                              <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>Towards time off</p>
                            </div>
                            <div>
                              <p className="font-heading text-lg font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(totalTimeOffFund)}</p>
                              <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>Total saved</p>
                            </div>
                            <div>
                              <p className="font-heading text-lg font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(totalTimeOffFund / weeksOff)}</p>
                              <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>For each week you take off</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="relative h-3 rounded-full overflow-hidden mb-8" style={{ background: 'rgba(42, 30, 92, 0.1)' }}>
                        <div className="absolute h-full" style={{ left: '0%', width: '33.33%', background: '#2A1E5C', borderRight: '2px solid rgba(255,255,255,0.3)' }} />
                        <div className="absolute h-full" style={{ left: '33.33%', width: '33.33%', background: '#2E86AB', borderRight: '2px solid rgba(255,255,255,0.3)' }} />
                        <div className="absolute h-full" style={{ left: '66.66%', width: '33.34%', background: '#4CAF50' }} />
                      </div>
                      <div className="grid md:grid-cols-3 gap-6">
                        <div className="p-6 rounded-xl flex flex-col" style={{ background: 'rgba(42, 30, 92, 0.08)', border: '2px solid #2A1E5C' }}>
                          <div className="flex items-center justify-between mb-6">
                            <span className="font-heading text-sm uppercase tracking-wider font-medium" style={{ color: '#2A1E5C' }}>Foundation</span>
                            <span className="font-body text-xs" style={{ color: '#8B7E6A' }}>Months 1-{options.phase1.endMonth}</span>
                          </div>
                          <div className="text-center flex-1 flex flex-col justify-center mb-6">
                            <p className="font-heading text-4xl lg:text-5xl font-medium mb-2" style={{ color: '#2A1E5C' }}>{formatCurrency(options.phase1.grossWeekly)}</p>
                            <p className="font-heading text-lg" style={{ color: '#5D5A6B' }}>per week</p>
                            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
                              <p className="font-heading text-2xl font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(options.phase1.grossDaily)}/day</p>
                              <p className="font-body text-sm mt-1" style={{ color: '#5D5A6B' }}>× {daysPerWeek} days a week</p>
                            </div>
                          </div>
                          <div className="space-y-2 mb-4 pt-4 border-t" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
                            <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>Each week you're setting aside:</p>
                            {options.phase1.activeGoals.map((goal) => (
                              <div key={`phase1-${goal.name}`} className="flex justify-between items-center">
                                <span className="font-body text-sm" style={{ color: '#5D5A6B' }}>{goal.name}</span>
                                <span className="font-body text-sm font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(goal.weeklyAmount)}/wk</span>
                              </div>
                            ))}
                          </div>
                          <div className="pt-4 border-t mt-auto text-center" style={{ borderColor: 'rgba(42, 30, 92, 0.1)' }}>
                            <CheckCircle2 className="w-6 h-6 mx-auto mb-2" style={{ color: '#2A1E5C' }} />
                            <span className="font-body text-sm" style={{ color: '#5D5A6B' }}>Debt cleared by</span>
                            <p className="font-heading text-2xl font-medium" style={{ color: '#2A1E5C' }}>Month {options.phase1.endMonth}</p>
                          </div>
                        </div>
                        <div className="p-6 rounded-xl flex flex-col" style={{ background: 'rgba(46, 134, 171, 0.08)', border: '2px solid #2E86AB' }}>
                          <div className="flex items-center justify-between mb-4">
                            <span className="font-heading text-sm uppercase tracking-wider font-medium" style={{ color: '#2E86AB' }}>Momentum</span>
                            <span className="font-body text-xs" style={{ color: '#8B7E6A' }}>Months {options.phase1.endMonth + 1}-12</span>
                          </div>
                          <p className="font-heading text-lg mb-4" style={{ color: '#2A1E5C' }}>Debt's done. Now you have options:</p>
                          <div className="p-4 rounded-lg mb-3 text-center" style={{ background: 'rgba(76, 175, 80, 0.15)' }}>
                            <p className="font-body text-xs font-medium mb-1" style={{ color: '#4CAF50' }}>Path A</p>
                            <p className="font-heading text-sm font-medium mb-2" style={{ color: '#2A1E5C' }}>Keep Your Momentum</p>
                            <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>{formatCurrency(options.optionA.dailyTarget)}/day × {options.daysPerWeek} days = {formatCurrency(options.optionA.weeklyTarget)}/wk</p>
                            <p className="font-body text-xs mt-1" style={{ color: '#5D5A6B' }}>Extra {formatCurrency(options.optionA.extraPerWeek)}/wk goes to savings</p>
                          </div>
                          <div className="p-4 rounded-lg mb-3 text-center" style={{ background: 'rgba(46, 134, 171, 0.15)' }}>
                            <p className="font-body text-xs font-medium mb-1" style={{ color: '#2E86AB' }}>Path B</p>
                            <p className="font-heading text-sm font-medium mb-2" style={{ color: '#2A1E5C' }}>Give Yourself Flexibility</p>
                            <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>Lower your target to {formatCurrency(options.optionB.weeklyTargetLow)}/wk – {formatCurrency(options.optionB.weeklyTargetHigh)}/wk</p>
                            <p className="font-body text-xs mt-1" style={{ color: '#5D5A6B' }}>You choose your pace</p>
                          </div>
                          <div className="p-4 rounded-lg mb-3 text-center" style={{ background: 'rgba(20, 184, 166, 0.15)' }}>
                            <p className="font-body text-xs font-medium mb-1" style={{ color: '#14B8A6' }}>Path C</p>
                            <p className="font-heading text-sm font-medium mb-2" style={{ color: '#2A1E5C' }}>Cover Your Expenses</p>
                            <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>{formatCurrency(options.optionC.dailyTarget)}/day × {options.optionC.daysPerWeek} days = {formatCurrency(options.optionC.weeklyTarget)}/wk</p>
                            <p className="font-body text-xs mt-1" style={{ color: '#5D5A6B' }}>Meet your savings goal exactly</p>
                          </div>
                          <div className="p-4 rounded-lg text-center" style={{ background: 'rgba(245, 203, 92, 0.18)' }} data-testid="buy-back-time-option">
                            <p className="font-body text-xs font-medium mb-1" style={{ color: '#B8952E' }}>Path D</p>
                            <p className="font-heading text-sm font-medium mb-2" style={{ color: '#2A1E5C' }}>Buy Back Time</p>
                            <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>{formatCurrency(options.optionD.dailyTarget)}/day × {options.optionD.daysPerWeek} days = {formatCurrency(options.optionD.weeklyTarget)}/wk</p>
                            <p className="font-body text-xs mt-1" style={{ color: '#5D5A6B' }}>Extra {formatCurrency(options.optionD.extraPerWeek)}/wk goes to your time-off fund</p>
                          </div>
                        </div>
                        <div className="p-6 rounded-xl flex flex-col" style={{ background: 'rgba(76, 175, 80, 0.08)', border: '2px solid #4CAF50' }}>
                          <div className="flex items-center justify-between mb-4">
                            <span className="font-heading text-sm uppercase tracking-wider font-medium" style={{ color: '#4CAF50' }}>Year-End Results</span>
                            <span className="font-body text-xs" style={{ color: '#8B7E6A' }}>At Month 12</span>
                          </div>
                          <p className="font-heading text-lg mb-4" style={{ color: '#2A1E5C' }}>Where each path lands you:</p>
                          <div className="p-4 rounded-lg mb-3 text-center" style={{ background: 'rgba(76, 175, 80, 0.15)' }}>
                            <p className="font-body text-xs font-medium mb-1" style={{ color: '#4CAF50' }}>Path A</p>
                            <p className="font-heading text-2xl font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(options.optionA.totalSavedByYearEnd)}</p>
                            <p className="font-body text-xs" style={{ color: '#5D5A6B' }}>in savings</p>
                            <p className="font-body text-xs mt-1" style={{ color: '#4CAF50' }}>(+{formatCurrency(options.optionA.extraSavings)} beyond goal)</p>
                          </div>
                          <div className="p-4 rounded-lg mb-3 text-center" style={{ background: 'rgba(46, 134, 171, 0.15)' }}>
                            <p className="font-body text-xs font-medium mb-1" style={{ color: '#2E86AB' }}>Path B</p>
                            <p className="font-heading text-lg font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(options.optionB.totalSavedLow)} – {formatCurrency(options.optionB.totalSavedHigh)}</p>
                            <p className="font-body text-xs" style={{ color: '#5D5A6B' }}>in savings</p>
                            <p className="font-body text-xs mt-1" style={{ color: '#8B7E6A' }}>(you choose your pace)</p>
                          </div>
                          <div className="p-4 rounded-lg mb-3 text-center" style={{ background: 'rgba(20, 184, 166, 0.15)' }}>
                            <p className="font-body text-xs font-medium mb-1" style={{ color: '#14B8A6' }}>Path C</p>
                            <p className="font-heading text-2xl font-medium" style={{ color: '#2A1E5C' }}>{formatCurrency(options.optionC.totalSavedByYearEnd)}</p>
                            <p className="font-body text-xs" style={{ color: '#5D5A6B' }}>in savings</p>
                            <p className="font-body text-xs mt-1" style={{ color: '#8B7E6A' }}>(goal met exactly)</p>
                          </div>
                          <div className="p-4 rounded-lg text-center" style={{ background: 'rgba(245, 203, 92, 0.18)' }}>
                            <p className="font-body text-xs font-medium mb-1" style={{ color: '#B8952E' }}>Path D</p>
                            <p className="font-heading text-2xl font-medium" style={{ color: '#2A1E5C' }}>+{options.optionD.extraWeeksOff.toFixed(1)} weeks off</p>
                            <p className="font-body text-xs" style={{ color: '#5D5A6B' }}>fully funded time off</p>
                            <p className="font-body text-xs mt-1" style={{ color: '#8B7E6A' }}>({formatCurrency(options.optionD.ptoFundByYearEnd)} to your time-off fund, goal still met)</p>
                          </div>
                          <p className="font-body text-xs text-center mt-auto pt-4" style={{ color: '#8B7E6A' }}>All paths clear your debt and meet your savings goal.</p>
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <SinglePaceTrajectory
                    weeklyTarget={weeklyTarget}
                    dailyTarget={dailyTarget}
                    daysPerWeek={daysPerWeek}
                    workWeeks={workWeeks}
                    setAsides={getGoalTimelines()}
                    timeOffBufferPerWeek={getTimeOffBuffer()}
                    formatCurrency={formatCurrency}
                  />
                );
              })()}

              {/* Format & Color picker */}
              <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6 mb-8">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(42, 30, 92, 0.08)' }}>
                  <span className="font-body text-xs" style={{ color: '#5D5A6B' }}>Format:</span>
                  {[
                    { id: 'landscape', label: 'Landscape' },
                    { id: 'portrait', label: 'Portrait' },
                    { id: 'story', label: 'Story' }
                  ].map((format) => (
                    <button
                      key={format.id}
                      onClick={() => setPlacardFormat(format.id)}
                      className={`px-3 py-1 rounded-full font-body text-xs transition-all ${
                        placardFormat === format.id
                          ? 'text-white'
                          : 'hover:opacity-80'
                      }`}
                      style={{
                        background: placardFormat === format.id ? '#2A1E5C' : 'transparent',
                        color: placardFormat === format.id ? '#FFFFFF' : '#5D5A6B'
                      }}
                      data-testid={`format-${format.id}`}
                    >
                      {format.label}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-3">
                  <Palette className="w-4 h-4" style={{ color: '#5D5A6B' }} />
                  {accentColors.map((color) => (
                    <button
                      key={color.hex}
                      onClick={() => setPlacardColor(color.hex)}
                      className={`w-8 h-8 rounded-full transition-all ${placardColor === color.hex ? 'ring-2 ring-gray-800 scale-110' : 'hover:scale-105'}`}
                      style={{ background: color.hex }}
                      title={color.name}
                      data-testid={`color-${color.name.toLowerCase().replace(/\s+/g, '-')}`}
                    />
                  ))}
                </div>
              </div>

              {/* Goal Card - Centered */}
              <div className="relative flex justify-center mb-8" data-testid="goal-card">
                {/* Main Placard */}
                <div
                  ref={placardRef}
                  className={`placard-artsy transition-all duration-300 ${
                    placardFormat === 'landscape' ? 'w-full max-w-2xl' :
                    placardFormat === 'portrait' ? 'w-full max-w-sm' :
                    'w-full max-w-xs'
                  }`}
                >
                  <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${placardColor} 0%, ${placardColor}dd 100%)`, padding: placardFormat === 'landscape' ? '28px 36px' : '20px 24px' }}>
                    <div className="absolute top-0 right-0 w-32 h-32 blob-1" style={{ background: 'rgba(255, 255, 255, 0.1)', transform: 'translate(30%, -30%)' }} />
                    <div className={`relative flex items-center ${placardFormat === 'landscape' ? 'justify-between' : 'justify-center'}`}>
                      <div className="flex items-center gap-3">
                        <Zap className={placardFormat === 'landscape' ? 'w-6 h-6' : 'w-5 h-5'} style={{ color: placardColor === '#F5CB5C' ? '#2A1E5C' : '#F5CB5C' }} />
                        <span className={`font-heading font-medium ${placardFormat === 'landscape' ? 'text-xl' : 'text-lg'}`} style={{ color: placardColor === '#F5CB5C' ? '#2A1E5C' : '#FFFFFF' }}>My Weekly Income Target</span>
                      </div>
                      {placardFormat === 'landscape' && (
                        <span className="font-body text-sm" style={{ color: placardColor === '#F5CB5C' ? '#2A1E5C' : '#F5CB5C' }}>
                          {selectedPaths.length} goal{selectedPaths.length > 1 ? 's' : ''}, one number
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ padding: placardFormat === 'landscape' ? '32px 40px' : placardFormat === 'portrait' ? '24px' : '20px' }}>
                    <p className={`font-body leading-relaxed mb-4 ${placardFormat === 'landscape' ? 'text-lg' : 'text-base'}`} style={{ color: '#2A1E5C' }}>
                      {getGoalStatementJSX()}
                    </p>
                    <div className={`rounded-2xl mb-4 ${placardFormat === 'landscape' ? 'p-6' : 'p-4'}`} style={{ background: 'rgba(42, 30, 92, 0.04)' }}>
                      <div className="text-center">
                        <p className="font-body text-xs uppercase tracking-widest mb-1" style={{ color: '#8B7E6A' }}>Gross Weekly Target</p>
                        <p className={`font-heading font-medium ${placardFormat === 'landscape' ? 'text-5xl' : placardFormat === 'portrait' ? 'text-4xl' : 'text-3xl'}`} style={{ color: placardColor }}>
                          {formatCurrency(calculateRelayTarget())}
                        </p>
                        <p className={`font-heading font-medium mt-2 ${placardFormat === 'landscape' ? 'text-2xl' : 'text-xl'}`} style={{ color: '#F5CB5C' }}>
                          {formatCurrency(calculateDailyTarget())}/day
                        </p>
                        <p className={`font-body font-medium mt-3 ${placardFormat === 'landscape' ? 'text-sm' : 'text-sm'}`} style={{ color: '#28A745' }}>
                          Net after taxes: {formatCurrency(getTaxBreakdown().netWeekly)}/week
                        </p>
                        <p className="font-body text-xs mt-1" style={{ color: '#A89F8F' }}>
                          {formData.daysPerWeek} days/week • {getWorkingWeeks()} weeks • {getWeeksOff()} weeks off
                        </p>
                        {getWeeksOff() > 0 && (
                          <div className="mt-2">
                            <p className="font-body text-xs font-medium" style={{ color: '#2E86AB' }}>
                              Time-off buffer: {formatCurrency(getTimeOffBuffer())}/week for {getWeeksOff()} weeks PTO
                            </p>
                            <p className="font-body text-xs mt-1 italic" style={{ color: '#8B8178' }}>
                              Your PTO bank is based on your <strong style={{ fontStyle: 'normal' }}>after-tax weekly target</strong>, so it replaces your take-home rhythm during time off without taxing the same money twice.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Payday Relay Logo */}
                    <div className="flex justify-center pt-4 border-t" style={{ borderColor: 'rgba(42, 30, 92, 0.08)' }}>
                      <img loading="lazy"
                        src="/images/paydayrelaylogo.png"
                        alt="Payday Relay"
                        className={placardFormat === 'landscape' ? 'h-16' : 'h-14'}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Export Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
                <button
                  onClick={downloadPNG}
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-heading font-medium text-base transition-all hover:scale-105"
                  style={{ background: '#F5CB5C', color: '#2A1E5C' }}
                  data-testid="download-png-btn"
                >
                  <Download className="w-5 h-5" />
                  Save as PNG
                </button>
                <button
                  onClick={downloadPDF}
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-heading font-medium text-base transition-all hover:scale-105"
                  style={{ background: 'rgba(42, 30, 92, 0.1)', color: '#2A1E5C', border: '2px solid rgba(42, 30, 92, 0.2)' }}
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-5 h-5" />
                  Save as PDF
                </button>
              </div>

              {/* Season Setup, lock recalibrated plan */}
              {seasonSetupMode && (
                <div className="max-w-xl mx-auto mb-6 p-5 rounded-2xl text-center" style={{ background: '#2A1E5C', border: '2px solid #F5CB5C' }} data-testid="season-setup-save-card">
                  <p className="font-heading text-base font-bold mb-1" style={{ color: '#F5CB5C' }}>Season Setup, Recalibrate My Relay</p>
                  <p className="font-body text-xs mb-4" style={{ color: 'rgba(255,255,255,0.75)' }}>
                    Happy with these numbers? Lock them in as your first-quarter plan and head back to start your race.
                  </p>
                  <button
                    onClick={handleSeasonSetupSave}
                    disabled={seasonSaving}
                    className="px-6 py-3 rounded-full font-heading font-bold text-sm transition-all hover:scale-105 disabled:opacity-50"
                    style={{ background: '#F5CB5C', color: '#2A1E5C' }}
                    data-testid="season-setup-save-plan-btn"
                  >
                    {seasonSaving ? 'Saving your plan…' : 'Start My Payday Relay Race →'}
                  </button>
                </div>
              )}

              {/* Pit Stop, carry the new weekly target back into the quarterly recalibration */}
              {pitStopMode && (
                <div className="max-w-xl mx-auto mb-6 p-5 rounded-2xl text-center" style={{ background: '#2A1E5C', border: '2px solid #F5CB5C' }} data-testid="pit-stop-lock-card">
                  <p className="font-heading text-xs font-bold uppercase tracking-[0.2em] mb-1" style={{ color: '#F5CB5C' }}>Pit Stop · recalibrate</p>
                  <p className="font-heading text-lg font-bold mb-1" style={{ color: '#FFFFFF' }}>Is this the pace for the quarter ahead?</p>
                  <p className="font-body text-xs mb-4" style={{ color: 'rgba(255,255,255,0.75)' }}>
                    Lock it in and we'll bring {formatCurrency(buildResultsPayload('member').weekly_target)}/week back into your quarterly tax day, where it gets written to your sheet for the remaining quarters.
                  </p>
                  <button
                    onClick={() => window.location.assign(`/app/reset?from=calculator&target=${Math.round(buildResultsPayload('member').weekly_target)}`)}
                    className="px-6 py-3 rounded-full font-heading font-bold text-sm transition-all hover:scale-105"
                    style={{ background: '#F5CB5C', color: '#2A1E5C' }}
                    data-testid="pit-stop-lock-btn"
                  >
                    Lock in my new numbers → back to quarterly tax day
                  </button>
                </div>
              )}

              {/* Unified funnel, Step 1 done, on to Step 2 (sync the sheet) */}
              {onboardingMode && (
                <div className="max-w-xl mx-auto mb-6 p-5 rounded-2xl text-center" style={{ background: '#2A1E5C', border: '2px solid #F5CB5C' }} data-testid="onboarding-lock-card">
                  <p className="font-heading text-xs font-bold uppercase tracking-[0.2em] mb-1" style={{ color: '#F5CB5C' }}>Step 1 of 3 · Calculate</p>
                  <p className="font-heading text-lg font-bold mb-1" style={{ color: '#FFFFFF' }}>Happy with these numbers?</p>
                  <p className="font-body text-xs mb-4" style={{ color: 'rgba(255,255,255,0.75)' }}>
                    Lock them in and we'll email you your PDF, then build your Payday Relay Google Sheet from them in Step 2.
                  </p>
                  <button
                    onClick={handleOnboardingLock}
                    disabled={lockSaving}
                    className="px-6 py-3 rounded-full font-heading font-bold text-sm transition-all hover:scale-105 disabled:opacity-50"
                    style={{ background: '#F5CB5C', color: '#2A1E5C' }}
                    data-testid="onboarding-lock-btn"
                  >
                    {lockSaving ? 'Saving your numbers…' : 'Lock in my relay numbers → next: sync your sheet'}
                  </button>
                </div>
              )}

              {/* Mid-quarter racer: the one place in here that actually writes their numbers */}
              {midQuarterChange && !onboardingMode && !pitStopMode && !seasonSetupMode && (
                <div className="max-w-xl mx-auto mb-6 p-6" style={{ background: '#FFFFFF', border: '1px solid #E0D9CB' }} data-testid="midquarter-lock-card">
                  <p className="font-body text-[11px] uppercase tracking-[0.2em] mb-2" style={{ color: '#8B7E6A' }}>
                    Make it official
                  </p>
                  <p className="font-heading text-lg font-bold mb-1" style={{ color: '#2A1E5C' }}>
                    Want these to be your real numbers?
                  </p>
                  <p className="font-body text-sm leading-relaxed mb-5" style={{ color: '#5D5A6B' }}>
                    Everything above is a scenario until you lock it in. Locking in replaces your current
                    weekly target for the rest of the race. Explore the numbers as much as you like, nothing
                    touches your sheet until you choose to sync, and it's best to keep debt and savings steady
                    through the year rather than changing them mid-race.
                  </p>
                  <button
                    onClick={() => setLockConfirmOpen(true)}
                    disabled={lockSaving}
                    className="font-body text-sm font-semibold px-8 py-3.5 transition-opacity hover:opacity-90 disabled:opacity-50"
                    style={{ background: '#2A1E5C', color: '#F2EFE7', borderRadius: 0 }}
                    data-testid="midquarter-lock-btn"
                  >
                    {lockSaving ? 'Saving your numbers…' : 'Lock in these numbers'}
                  </button>
                </div>
              )}

              {lockConfirmOpen && (
                <ConfirmNotice
                  testId="midquarter-lock-confirm"
                  eyebrow="Before you lock this in"
                  title="Are you sure you want to lock these numbers in?"
                  lead={`Your weekly target becomes ${formatCurrency(calculateRelayTarget())}, part-way through the quarter you are already running.`}
                  items={[
                    'Your weekly target for the rest of the race',
                    'Your progress bar and the weeks you have already raced, they get measured against the new number',
                    'The goal numbers written into your Google Sheet',
                  ]}
                  note="Heads up, try to keep your debt and savings numbers steady through the year. Changing them mid-race reshapes your whole sheet, so only sync new ones here when you really mean to. Nothing is deleted. Your logged earnings stay exactly as they are."
                  confirmLabel="Yes, lock them in"
                  cancelLabel="Cancel"
                  busy={lockSaving}
                  onConfirm={handleOnboardingLock}
                  onCancel={() => setLockConfirmOpen(false)}
                  secondary={{
                    label: 'Not now, take me to my next quarterly tax day instead',
                    onClick: () => window.location.assign('/app/reset'),
                  }}
                />
              )}

              {/* Email My Results, hidden in onboarding (the PDF is emailed on lock) */}
              {!onboardingMode && (
              <div className="max-w-xl mx-auto mb-6 p-5 rounded-2xl text-center" style={{ background: 'rgba(245, 203, 92, 0.12)', border: '2px solid rgba(245, 203, 92, 0.4)' }} data-testid="email-results-card">
                <p className="font-heading text-base font-bold mb-1" style={{ color: '#2A1E5C' }}>Email Me These Results</p>
                <p className="font-body text-xs mb-4" style={{ color: '#5D5A6B' }}>
                  Get your target, goal card, and 12-month trajectory as a PDF. No waiting.
                </p>
                {emailSent ? (
                  <p className="font-body text-sm font-bold flex items-center justify-center gap-2" style={{ color: '#4CAF50' }} data-testid="email-results-success">
                    <CheckCircle2 className="w-4 h-4" /> Sent to {resultEmail}
                  </p>
                ) : (
                  <form onSubmit={handleEmailResults} className="flex flex-col sm:flex-row gap-2 justify-center">
                    <input
                      type="email"
                      value={resultEmail}
                      onChange={(e) => setResultEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="flex-1 px-4 py-2.5 rounded-xl border-2 font-body text-sm focus:outline-none"
                      style={{ borderColor: 'rgba(42,30,92,0.2)', color: '#2A1E5C' }}
                      data-testid="email-results-input"
                    />
                    <button
                      type="submit"
                      disabled={emailSending}
                      className="px-5 py-2.5 rounded-xl font-heading font-bold text-sm transition-all hover:scale-105 disabled:opacity-50"
                      style={{ background: '#2A1E5C', color: '#FFFFFF' }}
                      data-testid="email-results-submit"
                    >
                      {emailSending ? 'Sending…' : 'Email My Results'}
                    </button>
                  </form>
                )}
              </div>
              )}

              {/* Scenario Compare - Save slots */}
              <div className="max-w-xl mx-auto mb-6 p-5 rounded-2xl text-center" style={{ background: 'rgba(46, 134, 171, 0.08)', border: '2px solid rgba(46, 134, 171, 0.25)' }} data-testid="scenario-save-card">
                <p className="font-heading text-base font-bold mb-1" style={{ color: '#2A1E5C' }}>
                  Compare Scenarios
                </p>
                <p className="font-body text-xs mb-4" style={{ color: '#5D5A6B' }}>
                  Save this plan, then hit Start Over, try different numbers, and save the other slot to compare side by side.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => handleSaveScenario('A')}
                    className="px-5 py-2.5 rounded-xl font-heading font-medium text-sm transition-all hover:scale-105"
                    style={{ background: '#2E86AB', color: '#FFFFFF' }}
                    data-testid="save-scenario-a-btn"
                  >
                    {scenarios.A ? 'Overwrite Scenario A' : 'Save as Scenario A'}
                  </button>
                  <button
                    onClick={() => handleSaveScenario('B')}
                    className="px-5 py-2.5 rounded-xl font-heading font-medium text-sm transition-all hover:scale-105"
                    style={{ background: 'rgba(46, 134, 171, 0.12)', color: '#2E86AB', border: '2px solid #2E86AB' }}
                    data-testid="save-scenario-b-btn"
                  >
                    {scenarios.B ? 'Overwrite Scenario B' : 'Save as Scenario B'}
                  </button>
                </div>
              </div>

              {/* Start Over Button - unlimited use */}
              <div className="flex flex-col items-center mt-6 mb-8">
                <button
                  onClick={resetCalculator}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg font-body text-sm transition-all hover:bg-gray-100"
                  style={{ color: '#5D5A6B', border: '1px solid rgba(42, 30, 92, 0.2)' }}
                  data-testid="reset-btn"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Start Over
                </button>
                <p className="font-body text-xs mt-3" style={{ color: '#8B7E6A' }}>
                  Use it as many times as you want.
                </p>
              </div>

            </div>
          )}

          {/* Scenario Compare - visible in both wizard and results views */}
          <ScenarioCompare
            scenarios={scenarios}
            onLoad={handleLoadScenario}
            onClear={handleClearScenario}
            formatCurrency={formatCurrency}
          />
        </div>
      </main>

      {!embedded && <SiteFooter />}
    </div>
  );
};

export default RelayCalculatorPage;
